import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Play, Pause, X, RefreshCw, Music, Type } from 'lucide-react';
import { useVideoPageStateMachine } from '../hooks/useVideoPageStateMachine';
import { chatCompletion, resetCurrentProjectId, getHistoryProject, generateImage, generateTTS, createVideoTask, getVoices, getImageSizes, saveSceneList as saveSceneListApi, getBgMusicList, getSubtitleTemplates } from '../utils/api';
import { videoService } from '../services/VideoService';
import { PureFrontendVideoPlayer } from '../utils/PureFrontendVideoPlayer';
import { getVideoDuration } from '../utils/videoUtils';
import Toast from '../components/Toast';
import { useTranslation } from 'react-i18next';
import BgMusicSelector, { BgMusic } from '../components/BgMusicSelector';

// 导入对话框组件
import { TextToImageDialog, VoiceSynthesisDialog, ImageSelectionDialog, ImageToVideoDialog, DefaultImageIcon } from '../components/common';
import TransitionSettingDialog from '../components/common/TransitionSettingDialog';
import { SubtitleStyleDialog } from '../components/subtitle';
import type { SubtitleStyle, SubtitleTemplate } from '../types';

// 默认字幕样式常量
const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  fontName: 'Arial',
  fontSize: 20,
  primaryColor: '#FFFFFF',
  outlineColor: '#000000',
  outlineWidth: 2,
  alignment: 'bottom',
  marginV: 20,
  bold: false,
  italic: false
};

// 背景音乐选择对话框组件
const BgMusicDialog = ({ isOpen, onClose, children, t }: { isOpen: boolean; onClose: () => void; children: React.ReactNode; t: (key: string) => string }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div
        className="relative bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-4 shadow-2xl overflow-hidden w-[600px] h-[500px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 border-b border-gray-700/50 pb-2">
          <h2 className="text-xl font-semibold text-white">{t('inputBar.bgMusic.title')}</h2>
          <button
            className="text-gray-300 hover:text-white transition-colors"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl p-6 h-[calc(100%-72px)] overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// 分镜状态枚举
enum SceneStatus {
  LOADING = 'loading',           // 正在接收流式数据
  READY = 'ready',               // 数据完整，可编辑
  GENERATING_MEDIA = 'generating_media', // 正在生成图片/音频
  ERROR = 'error',               // 生成失败
  SELECTING = 'selecting'        // 选择中状态
}

// 游标位置接口
interface CursorPosition {
  startTime: number; // 开始时间，0-20s
  endTime: number;   // 结束时间，0-20s
}

// 转场配置接口
export interface TransitionConfig {
  type: string;          // 转场类型（如fade、slide_left等）
  duration: number;      // 转场时长（毫秒）
  direction?: string;    // 转场方向（可选）
  easing?: string;       // 缓动函数（可选）
  color?: string;        // 转场颜色（仅适用于wipe类型，可选）
}

// 分镜接口定义
interface SceneItem {
  id: string;
  desc: string;
  prompt: string;
  dialogue: string;
  images: string[];
  videoUrl: string;
  audioUrl: string;
  status: SceneStatus;
  videoTaskId?: string; // 视频生成任务ID
  videoStatus?: string; // 视频生成状态
  videoDuration?: number; // 视频时长（秒）

  // 转场配置
  transition?: TransitionConfig;

  // UI相关状态
  cursorPosition?: CursorPosition;
  audioPlayState?: boolean;
  videoLoading?: boolean;
  isImageGenerating?: boolean; // 标记是否正在生成或更新图片

  // 场景独立的语音设置（可选，未设置时使用全局值）
  voiceSpeed?: number;
  selectedVoice?: string;
}

// 生成唯一ID
const generateUniqueId = () => {
  return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// VideoEditPage ref 接口定义
export interface VideoEditPageRef {
  reloadPlayerAndPlayFromScene: (sceneIndex?: number) => void;
  getSceneList: () => SceneItem[];
}

const VideoEditPage = forwardRef<VideoEditPageRef>((_, ref) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [sceneList, _setSceneList] = useState<SceneItem[]>([]);

  // 智能包装setSceneList方法，使用useCallback保持引用稳定，在内部调用saveSceneList
  const setSceneList = React.useCallback((newSceneList: SceneItem[] | ((prev: SceneItem[]) => SceneItem[])) => {
    _setSceneList((prevSceneList) => {
      // 计算更新后的场景列表
      const updatedSceneList = typeof newSceneList === 'function' ? newSceneList(prevSceneList) : newSceneList;
      // 调用saveSceneList，传入最新的sceneList，避免闭包问题
      console.log('[TITLE_DEBUG] setSceneList触发，当前title值:', title);
      console.log('[AutoSave] setSceneList触发，调用saveSceneList保存最新场景列表');
      saveSceneList(undefined, updatedSceneList);
      return updatedSceneList;
    });
  }, []); // 依赖项为空，确保setSceneList的引用稳定
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const sseReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const stateMachine = useVideoPageStateMachine();

  // 从location.state获取配置选项
  const inputContent = location.state?.inputContent || '';
  const initialQuality = location.state?.quality || 'standard';
  const initialResolution = location.state?.resolution || '864x1536';
  const initialSelectedVoice = location.state?.selectedVoice || 'BV001_streaming';
  const initialSelectedVoiceType = location.state?.selectedVoiceType || initialSelectedVoice;
  const isEmptyScript = location.state?.isEmptyScript || false;
  
  // 背景音乐相关配置
  const [selectedBgMusic, _setSelectedBgMusic] = useState<string | null>(location.state?.selectedBgMusic || null);
  const [bgMusicVolume, _setBgMusicVolume] = useState<number>(location.state?.bgMusicVolume || 0.5);
  const [bgMusicList, setBgMusicList] = useState<BgMusic[]>([]);
  const [isBgMusicDialogOpen, setIsBgMusicDialogOpen] = useState(false);

  // 语速相关配置
  const [voiceSpeed, _setVoiceSpeed] = useState<number>(location.state?.voiceSpeed || 1.0);

  // 字幕样式配置（新增）
  const [subtitleStyle, _setSubtitleStyle] = useState<SubtitleStyle>(
    location.state?.subtitleStyle || DEFAULT_SUBTITLE_STYLE
  );
  const [isSubtitleDialogOpen, setIsSubtitleDialogOpen] = useState(false);

  // 字幕模板列表（新增）
  const [subtitleTemplates, setSubtitleTemplates] = useState<SubtitleTemplate[]>([]);

  // 🔧 使用 ref 保存 sceneList 最新值，解决 useCallback 和 saveSceneList 的闭包陷阱
  const sceneListRef = useRef<SceneItem[]>([]);

  // 智能包装setSelectedBgMusic方法，使用useCallback保持引用稳定，在内部调用saveSceneList
  const setSelectedBgMusic = React.useCallback((newSelectedBgMusic: string | null) => {
    _setSelectedBgMusic(newSelectedBgMusic);
    console.log('[AutoSave] setSelectedBgMusic触发，调用saveSceneList保存最新背景音乐选择');
    // 🔧 显式传入最新参数（从ref读取），避免闭包陷阱
    // 🔧 修复：传递所有配置参数，避免丢失字幕样式和语速
    saveSceneList(
      undefined,
      sceneListRef.current,
      newSelectedBgMusic,
      bgMusicVolume,
      voiceSpeed,
      subtitleStyle
    );
  }, [bgMusicVolume, voiceSpeed, subtitleStyle]);

  // 智能包装setBgMusicVolume方法，使用useCallback保持引用稳定，在内部调用saveSceneList
  const setBgMusicVolume = React.useCallback((newBgMusicVolume: number) => {
    _setBgMusicVolume(newBgMusicVolume);
    console.log('[AutoSave] setBgMusicVolume触发，调用saveSceneList保存最新背景音乐音量');
    // 🔧 显式传入最新参数（从ref读取），避免闭包陷阱
    // 🔧 修复：传递所有配置参数，避免丢失字幕样式和语速
    saveSceneList(
      undefined,
      sceneListRef.current,
      { url: selectedBgMusic, volume: newBgMusicVolume },
      voiceSpeed,
      subtitleStyle
    );
  }, [selectedBgMusic, voiceSpeed, subtitleStyle]);

  // 智能包装setVoiceSpeed方法，使用useCallback保持引用稳定，在内部调用saveSceneList
  const setVoiceSpeed = React.useCallback((newVoiceSpeed: number) => {
    _setVoiceSpeed(newVoiceSpeed);
    console.log('[AutoSave] setVoiceSpeed触发，调用saveSceneList保存最新语速');
    // 🔧 显式传入最新参数（从ref读取），避免闭包陷阱
    // 🔧 修复：传递所有配置参数，避免丢失字幕样式和背景音乐
    saveSceneList(
      undefined,
      sceneListRef.current,
      { url: selectedBgMusic, volume: bgMusicVolume },
      newVoiceSpeed,
      subtitleStyle
    );
  }, [selectedBgMusic, bgMusicVolume, subtitleStyle]);

  // 智能包装setSubtitleStyle方法，使用useCallback保持引用稳定，在内部调用saveSceneList（新增）
  const setSubtitleStyle = React.useCallback((newSubtitleStyle: SubtitleStyle) => {
    console.log('🎯 [setSubtitleStyle] 更新字幕样式:', {
      templateId: newSubtitleStyle.templateId,
      fontName: newSubtitleStyle.fontName,
      fontSize: newSubtitleStyle.fontSize,
      primaryColor: newSubtitleStyle.primaryColor
    });
    _setSubtitleStyle(newSubtitleStyle);
    console.log('[AutoSave] setSubtitleStyle触发，调用saveSceneList保存最新字幕样式');
    // 🔧 显式传入最新参数（从ref读取），避免闭包陷阱
    // 🔧 修复：传递 bgMusic 和 voiceSpeed，避免保存字幕时丢失这些配置
    saveSceneList(
      undefined,
      sceneListRef.current,
      { url: selectedBgMusic, volume: bgMusicVolume },
      voiceSpeed,
      newSubtitleStyle
    );
  }, [selectedBgMusic, bgMusicVolume, voiceSpeed]);

  // 🔧 同步 sceneList 到 ref
  useEffect(() => {
    sceneListRef.current = sceneList;
    console.log('[Ref同步] sceneListRef 已更新，长度:', sceneList.length);
  }, [sceneList]);

  // 🆕 加载字幕模板列表
  useEffect(() => {
    const fetchSubtitleTemplates = async () => {
      console.log('🔵 [SUBTITLE_DEBUG] 开始加载字幕模板');
      try {
        const response = await getSubtitleTemplates();
        const templates = response.templates || [];
        setSubtitleTemplates(templates);
        console.log('🔵 [SUBTITLE_DEBUG] 模板加载成功, 共', templates.length, '个');
      } catch (error) {
        console.error('🔴 [SUBTITLE_DEBUG] ❌ 加载失败:', error);
        setSubtitleTemplates([]);
      }
    };
    fetchSubtitleTemplates();
  }, []); // 只在组件挂载时执行一次

  // 播放器相关 refs - 参考 VideoPage
  const videoPlayerContainer = useRef<HTMLDivElement>(null);
  const videoPlayerInstance = useRef<any>(null);

  // 监听背景音乐选择和音量变化，实时更新播放器
  useEffect(() => {
    if (videoPlayerInstance.current) {
      console.log('🎬 [VIDEOEDIT] 背景音乐或音量变化，更新播放器', { selectedBgMusic, bgMusicVolume });
      videoPlayerInstance.current.updateBgMusic(selectedBgMusic, bgMusicVolume);
    }
  }, [selectedBgMusic, bgMusicVolume]);

  // 监听字幕样式变化，实时更新播放器（新增）
  useEffect(() => {
    if (videoPlayerInstance.current && subtitleStyle) {
      console.log('🎬 [VIDEOEDIT] 字幕样式变化，更新播放器', subtitleStyle);
      videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
    }
  }, [subtitleStyle]);

  // 分镜操作函数
  const handleDeleteScene = (sceneId: string) => {
    console.log('🗑️ [handleDeleteScene] 删除场景:', sceneId);

    setSceneList(prev => {
      const updatedList = prev.filter(item => item.id !== sceneId);
      console.log('🗑️ [handleDeleteScene] 删除后的场景列表长度:', updatedList.length);
      return updatedList;
    });
  };

  // 音频实例管理
  const audioInstancesRef = useRef<{ [key: string]: HTMLAudioElement | null }>({});

  // API 保存频控相关Ref
  const apiSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // 新增：用于数据diff和时间判定的ref
  const lastSavedSceneListRef = useRef<SceneItem[]>([]);
  // 新增：保存完整的数据（包含title、bgMusic、sceneList），用于准确比较diff
  const lastSavedDataRef = useRef<any>(null);
  const lastSaveTimeRef = useRef<number>(0);

  // ==================== 游标位置管理功能 ====================

  // 更新游标位置（拖拽时只更新Ref，避免状态异步问题）
  const updateCursorPosition = (sceneId: string, type: 'start' | 'end', value: number, fromDrag: boolean = false) => {
    // 确保值在0-20范围内并保留两位小数
    const clampedValue = Math.max(0, Math.min(20, Number(value.toFixed(2))));

    // 先更新Ref
    const currentPosition = latestCursorPositionsRef.current[sceneId] || { startTime: 0, endTime: 0 };

    let newPosition: CursorPosition;

    if (type === 'start') {
      // 开始时间始终为0，不允许调整
      newPosition = {
        startTime: 0,
        endTime: currentPosition.endTime // 保持右侧游标位置不变
      };
    } else {
      // 右侧游标（结束时间）移动时，直接调整结束时间，开始时间保持为0
      const newEndTime = clampedValue;
      // 确保结束时间不小于开始时间
      const actualEndTime = Math.max(0, newEndTime);

      newPosition = {
        startTime: 0,
        endTime: actualEndTime
      };
    }

    // 只有当位置实际发生变化时才继续处理
    const hasChanged = currentPosition.startTime !== newPosition.startTime || currentPosition.endTime !== newPosition.endTime;
    if (!hasChanged) {
      return; // 没有变化，直接返回
    }

    // 更新Ref（保持最新的游标位置）
    const newCursorPositions = { ...latestCursorPositionsRef.current, [sceneId]: newPosition };
    latestCursorPositionsRef.current = newCursorPositions;

    // 只有在拖拽结束或非拖拽场景下才更新状态，避免频繁重渲染
    if (!fromDrag || !isDraggingRef.current) {
      setSceneList(prev => {
        const updatedList = prev.map(item =>
          item.id === sceneId ? { ...item, cursorPosition: newPosition } : item
        );
        return updatedList;
      });
    }
  };

  // 新增：同步游标位置到sceneList状态（用于在拖拽结束时调用）
  const syncCursorToSceneList = (sceneId: string) => {
    const latestPosition = latestCursorPositionsRef.current[sceneId];
    if (latestPosition) {
      setSceneList(prev => {
        const updatedList = prev.map(item =>
          item.id === sceneId ? { ...item, cursorPosition: latestPosition } : item
        );
        return updatedList;
      });
    }
  };

  // ==================== 添加分镜功能 ====================

  // 在指定分镜后添加新分镜
  const handleAddItemAfterId = (itemId: string) => {
    const newId = generateUniqueId();
    const newItem: SceneItem = {
      id: newId,
      desc: '',
      prompt: '',
      dialogue: '',
      images: [],
      videoUrl: '',
      audioUrl: '',
      status: SceneStatus.READY,
      cursorPosition: { startTime: 0, endTime: 5 },
      audioPlayState: false,
      videoLoading: false
    };

    setSceneList(prev => {
      const insertAfterIndex = prev.findIndex(item => item.id === itemId);
      const newList = [...prev];
      if (insertAfterIndex === -1) {
        newList.push(newItem);
      } else {
        newList.splice(insertAfterIndex + 1, 0, newItem);
      }
      return newList;
    });
  };

  // 切换音频播放状态
  const toggleAudioPlayState = (sceneId: string) => {
    setSceneList(prev => {
      const targetScene = prev.find(item => item.id === sceneId);
      if (!targetScene) return prev;

      // 检查是否有有效的音频URL
      if (!targetScene.audioUrl || targetScene.audioUrl === '') {
        console.warn('🎵 没有有效的音频URL，无法播放:', sceneId);
        return prev;
      }

      const newPlayState = !targetScene.audioPlayState;

      // 获取或创建音频实例
      let audio = audioInstancesRef.current[sceneId];
      if (!audio) {
        audio = new Audio(targetScene.audioUrl);
        audioInstancesRef.current[sceneId] = audio;

        // 监听播放完成事件
        audio.addEventListener('ended', () => {
          console.log('🎵 音频播放完成:', sceneId);
          setSceneList(currentList =>
            currentList.map(item =>
              item.id === sceneId ? { ...item, audioPlayState: false } : item
            )
          );
        });

        // 监听播放错误
        audio.addEventListener('error', () => {
          console.error('🎵 音频播放错误:', sceneId);
          setSceneList(currentList =>
            currentList.map(item =>
              item.id === sceneId ? { ...item, audioPlayState: false } : item
            )
          );
        });
      }

      // 执行播放/暂停操作
      if (newPlayState) {
        audio.play().catch(error => {
          console.error('🎵 音频播放失败:', error);
          setSceneList(currentList =>
            currentList.map(item =>
              item.id === sceneId ? { ...item, audioPlayState: false } : item
            )
          );
        });
      } else {
        audio.pause();
      }

      // 返回更新后的场景列表
      return prev.map(item =>
        item.id === sceneId ? { ...item, audioPlayState: newPlayState } : item
      );
    });
  };

  // 时间控制相关的Ref
  const latestCursorPositionsRef = useRef<{ [key: string]: CursorPosition }>({});
  const cursorSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef<boolean>(false); // 拖拽状态标志
  const skipNextSaveRef = useRef<boolean>(false); // 跳过下次保存标记
  const progressBarFillRefs = useRef<{ [key: string]: HTMLElement }>({}); // 进度条填充部分的DOM引用

  // 对话框相关状态
  const [isTextToImageDialogOpen, setIsTextToImageDialogOpen] = useState(false);
  const [isVoiceSynthesisDialogOpen, setIsVoiceSynthesisDialogOpen] = useState(false);
  const [isImageSelectionDialogOpen, setIsImageSelectionDialogOpen] = useState(false);
  const [isImageToVideoDialogOpen, setIsImageToVideoDialogOpen] = useState(false);
  const [isTitleEditDialogOpen, setIsTitleEditDialogOpen] = useState(false);
  // 新增转场设置对话框状态
  const [isTransitionSettingDialogOpen, setIsTransitionSettingDialogOpen] = useState(false);
  const [currentSceneId, setCurrentSceneId] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [currentNarration, setCurrentNarration] = useState('');
  const [currentImageId, setCurrentImageId] = useState(1);
  const [dialogImages, setDialogImages] = useState<string[]>([]);
  const [editTitle, setEditTitle] = useState<string>('');

  // Prompt dialog state
  const [resolution, setResolution] = useState(initialResolution);
  const [quality, setQuality] = useState(initialQuality);
  const [imageSizes, setImageSizes] = useState<string[]>(['1024x1024', '1024x1364', '864x1536', '1364x1024']);

  // Voice-related state
  const [voices, setVoices] = useState<{ voice_id: string; name: string; voice_type?: string; audio_url: string }[]>([]);
  const [selectedVoice, setSelectedVoice] = useState(initialSelectedVoice);
  const [selectedVoiceType, setSelectedVoiceType] = useState(initialSelectedVoiceType);

  // 组件挂载时获取图片尺寸
  useEffect(() => {
    const fetchImageSizes = async () => {
      try {
        const response = await getImageSizes();
        if (response.code === 200 && response.data?.sizes) {
          setImageSizes(response.data.sizes);
        } else {
          // 接口失败时使用默认尺寸
          const defaultSizes = ['1024x1024', '1024x1364', '864x1536', '1364x1024'];
          setImageSizes(defaultSizes);
        }
      } catch (error) {
        // 异常时使用默认尺寸
        const defaultSizes = ['1024x1024', '1024x1364', '864x1536', '1364x1024'];
        setImageSizes(defaultSizes);
      }
    };
    fetchImageSizes();
  }, []);

  // 组件挂载时获取背景音乐列表
  useEffect(() => {
    const fetchBgMusicList = async () => {
      try {
        const response = await getBgMusicList();
        const responseData = response as any;
        let foundBgMusicList: BgMusic[] = [];
        if (Array.isArray(responseData)) {
          foundBgMusicList = responseData;
        } else if (responseData.musicList) {
          foundBgMusicList = responseData.musicList;
        } else if (responseData.data?.musicList) {
          foundBgMusicList = responseData.data.musicList;
        }
        setBgMusicList(foundBgMusicList);
      } catch (error) {
        console.error('获取背景音乐列表失败:', error);
        setBgMusicList([]);
      }
    };
    fetchBgMusicList();
  }, []);

  // 组件挂载时获取音色列表
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await getVoices();
        const responseData = response as any;

        let foundVoices: Array<{ voice_id: string; name: string; voice_type?: string }> = [];

        // 处理不同的API响应格式
        if (Array.isArray(responseData)) {
          foundVoices = responseData;
        } else if (responseData.voices) {
          foundVoices = responseData.voices;
        } else if (responseData.data?.voices) {
          foundVoices = responseData.data.voices;
        } else if (responseData.data?.data?.voices) {
          foundVoices = responseData.data.data.voices;
        }

        if (foundVoices.length > 0) {
          const formattedVoices = foundVoices
            .filter((voice: any) => voice.voice_id && voice.name)
            .map((voice: any) => ({
              voice_id: voice.voice_id,
              name: voice.name,
              voice_type: voice.voice_type || voice.voice_id,
              audio_url: voice.audio_url || ''
            }));

          setVoices(formattedVoices);

          // 更新selectedVoiceType，如果当前选中的voice_id存在于新的音色列表中
          const selectedVoiceObj = formattedVoices.find(v => v.voice_id === selectedVoice);
          if (selectedVoiceObj) {
            setSelectedVoiceType(selectedVoiceObj.voice_type);
          } else if (formattedVoices.length > 0) {
            // 如果当前选中的voice_id不存在，则默认选择第一个
            setSelectedVoice(formattedVoices[0].voice_id);
            setSelectedVoiceType(formattedVoices[0].voice_type);
          }
        } else {
          // 接口返回空数据时，使用空数组
          setVoices([]);
          setSelectedVoice('');
          setSelectedVoiceType('');
        }
      } catch (error) {
        console.error('获取语音列表失败:', error);
        // 异常时使用空数组
        setVoices([]);
        setSelectedVoice('');
        setSelectedVoiceType('');
      }
    };

    fetchVoices();
  }, []);

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // 视频标题
  const [title, _setTitle] = useState<string>("");
  // 新增：用于立即访问title的ref，避免异步更新问题
  const titleRef = useRef<string>("");
  
  // 智能包装setTitle方法，使用useCallback保持引用稳定，在内部调用saveSceneList
  const setTitle = React.useCallback((newTitle: string) => {
    _setTitle(newTitle);
    titleRef.current = newTitle;
    console.log('[AutoSave] setTitle触发，调用saveSceneList保存最新标题');
    // 🔧 显式传入最新参数（从ref读取），避免闭包陷阱
    // 🔧 修复：传递所有配置参数，避免丢失背景音乐、语速和字幕样式
    saveSceneList(
      newTitle,
      sceneListRef.current,
      { url: selectedBgMusic, volume: bgMusicVolume },
      voiceSpeed,
      subtitleStyle
    );
  }, [selectedBgMusic, bgMusicVolume, voiceSpeed, subtitleStyle]);

  // 获取工程ID
  const projectId = searchParams.get('projectId');

  // 流式数据请求
  const streamChatRequest = async (content: string) => {
    console.log('[STATE_MACHINE] 🚀 streamChatRequest called in VideoEditPage');

    // 使用状态机方法替代原有的状态设置
    stateMachine.startGeneration();

    setSceneList([]);

    // 🔧 关键修复：根据是否有projectId决定是否重置
    if (projectId) {
      console.log('🔄 [streamChatRequest] 保持历史项目ID:', projectId);
      // 不要重置，保持现有的projectId
    } else {
      console.log('🔄 [streamChatRequest] 重置当前项目ID，开始新项目');
      resetCurrentProjectId();
      // 🔧 关键修复：重置lastSavedDataRef，确保新项目的diff判断正确
      lastSavedDataRef.current = null;
    }

    try {
      const response = await chatCompletion({
        messages: [{ role: 'user', content }],
        stream: true,
        promptId: 'video_storyboard',
        model: 'deepseek-chat'
      });

      console.log('[SSE_DEBUG] 1. Response received', response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();

      console.log('[SSE_DEBUG] 2. Reader obtained', reader);
      console.log('[TITLE_DEBUG] 开始解析SSE流，准备提取title');

      if (!reader) {
        throw new Error('No readable stream from response');
      }

      // 保存reader引用
      sseReaderRef.current = reader;

      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let accumulatedContent = '';

      // 解析状态
      let currentSceneId: string | null = null;
      let currentSceneData: Partial<SceneItem> = {};

      // 使用while循环替代递归调用，与VideoPage保持一致
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        console.log('[TITLE_DEBUG] 接收到SSE数据块:', chunk);

        // SSE事件以两个换行符分隔
        const sseEvents = buffer.split(/\n\n/);
        buffer = sseEvents.pop() || '';
        console.log('[TITLE_DEBUG] 解析出', sseEvents.length, '个SSE事件');
        console.log('[TITLE_DEBUG] 剩余缓冲区:', buffer);

        for (const event of sseEvents) {
          if (!event.trim()) continue;

          const eventLines = event.split('\n');
          let eventType = '';
          let eventData = '';

          // 解析事件的每一行
          for (const line of eventLines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              eventData += line.slice(5).trim();
            }
          }

          console.log('[SSE_DEBUG] 4. Processing event:', eventType, eventData);

          // 检查是否是关闭连接的消息
          if (eventData.includes('[DONE]') || event.includes('[DONE]')) {
            console.log('[SSE_DEBUG] 6. Received [DONE] message, stream completed');
            sseReaderRef.current?.cancel();
            sseReaderRef.current = null;
            break;
          }

          // 解析JSON数据
          try {
            if (eventType === 'message' && eventData) {
              const data = JSON.parse(eventData);
              console.log('[SSE_DEBUG] 解析到JSON:', data);

              if (data.message?.content) {
                const content = data.message.content;
                if (!content) continue;

                console.log('[SSE_DEBUG] OpenAI格式 content:', content);
                accumulatedContent += content;

                // 按行分割，保留最后一行可能不完整的内容
                const contentLines = accumulatedContent.split('\n');
                accumulatedContent = contentLines.pop() || '';
                console.log('[TITLE_DEBUG] 分割出', contentLines.length, '行内容，剩余', accumulatedContent.length, '个字符');

                // 处理每一行
                for (const contentLine of contentLines) {
                  console.log('[TITLE_DEBUG] 处理行:', contentLine);
                  if (!contentLine.trim()) continue;

                  // 处理标题
                  if (contentLine.includes('**title**:')) {
                    console.log('[TITLE_DEBUG] 发现title行:', contentLine);
                    // 查找title标记的位置，然后从该位置开始提取
                    const titleIndex = contentLine.indexOf('**title**:');

                    if (titleIndex !== -1) {
                      // 从title标记位置后10个字符开始提取（即冒号后的内容）
                      const parsedTitle = contentLine.slice(titleIndex + 10).trim();
                      console.log('[TITLE_DEBUG] 解析到标题:', parsedTitle);
                      setTitle(parsedTitle); // 保存标题到state
                      titleRef.current = parsedTitle; // 立即保存到ref，避免异步问题
                      console.log('[TITLE_DEBUG] 调用setTitle设置标题');
                      console.log('[TITLE_DEBUG] titleRef.current:', titleRef.current);
                      console.log('[TITLE_DEBUG] 调用setTitle设置标题');
                      console.log('[TITLE_DEBUG] titleRef.current:', titleRef.current);
                    }
                    continue;
                  }

                  // 处理新场景开始标记
                  if (contentLine.startsWith('## scene')) {
                    console.log('[TITLE_DEBUG] 发现场景行:', contentLine);
                    console.log('[TITLE_DEBUG] 当前title值:', title);
                    // 创建新的loading态分镜
                    console.log('[SSE_DEBUG] 新场景开始:', contentLine);
                    currentSceneId = createLoadingScene();
                    currentSceneData = {
                      desc: '',
                      prompt: '',
                      dialogue: ''
                    };
                    console.log('[TITLE_DEBUG] 调用createLoadingScene创建新场景，会触发setSceneList');
                    continue;
                  }

                  // 处理场景字段
                  if (currentSceneId) {
                    if (contentLine.startsWith('**desc**:')) {
                      // **desc**: 共9个字符
                      const fieldValue = contentLine.slice(9).trim();
                      console.log('[SSE_DEBUG] 解析desc:', fieldValue);
                      currentSceneData.desc = fieldValue;
                    } else if (contentLine.startsWith('**prompt**:')) {
                      // **prompt**: 共11个字符
                      const fieldValue = contentLine.slice(11).trim();
                      console.log('[SSE_DEBUG] 解析prompt:', fieldValue);
                      currentSceneData.prompt = fieldValue;
                    } else if (contentLine.startsWith('**dialogue**:')) {
                      // 使用正确的切片长度："**dialogue**:" 共13个字符
                      const fieldValue = contentLine.slice(13).trim();
                      console.log('[SSE_DEBUG] 解析dialogue:', fieldValue);
                      currentSceneData.dialogue = fieldValue;

                      // dialogue完成，分镜数据完整
                      if (currentSceneData.prompt && fieldValue) {
                        console.log('[SSE_DEBUG] 场景完整，更新状态，场景ID:', currentSceneId);
                        updateSceneReady(currentSceneId, currentSceneData);
                      }
                    } else {
                      // 累积同一个字段的后续内容
                      if (currentSceneData.desc && !contentLine.startsWith('**')) {
                        console.log('[SSE_DEBUG] 累积desc:', contentLine);
                        currentSceneData.desc += '\n' + contentLine;
                      } else if (currentSceneData.prompt && !contentLine.startsWith('**')) {
                        console.log('[SSE_DEBUG] 累积prompt:', contentLine);
                        currentSceneData.prompt += '\n' + contentLine;
                      } else if (currentSceneData.dialogue && !contentLine.startsWith('**')) {
                        console.log('[SSE_DEBUG] 累积dialogue:', contentLine);
                        currentSceneData.dialogue += '\n' + contentLine;
                      }
                    }
                  }
                }
              }
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError);
          }
        }
      }

      // 处理缓冲区中剩余的内容
      if (accumulatedContent.trim()) {
        const contentLines = accumulatedContent.split('\n');
        for (const contentLine of contentLines) {
          if (!contentLine.trim()) continue;

          // 处理标题
          if (contentLine.includes('**title**:')) {
            // 查找title标记的位置，然后从该位置开始提取
            const titleIndex = contentLine.indexOf('**title**:');

            if (titleIndex !== -1) {
              // 从title标记位置后10个字符开始提取（即冒号后的内容）
              const parsedTitle = contentLine.slice(titleIndex + 10).trim();
              console.log('[TITLE_DEBUG] 解析到标题:', parsedTitle);
              setTitle(parsedTitle); // 保存标题到state
              titleRef.current = parsedTitle; // 立即保存到ref，避免异步问题
            }
            continue;
          }

          // 处理新场景开始标记
          if (contentLine.startsWith('## scene')) {
            // 创建新的loading态分镜
            console.log('[SSE_DEBUG] 新场景开始:', contentLine);
            currentSceneId = createLoadingScene();
            currentSceneData = {
              desc: '',
              prompt: '',
              dialogue: ''
            };
            continue;
          }

          // 处理场景字段
          if (currentSceneId) {
            if (contentLine.startsWith('**desc**:')) {
              // **desc**: 共9个字符
              const fieldValue = contentLine.slice(9).trim();
              console.log('[SSE_DEBUG] 解析desc:', fieldValue);
              currentSceneData.desc = fieldValue;
            } else if (contentLine.startsWith('**prompt**:')) {
              // **prompt**: 共11个字符
              const fieldValue = contentLine.slice(11).trim();
              console.log('[SSE_DEBUG] 解析prompt:', fieldValue);
              currentSceneData.prompt = fieldValue;
            } else if (contentLine.startsWith('**dialogue**:')) {
              // 修复：使用正确的切片长度13，而不是12
              const fieldValue = contentLine.slice(13).trim();
              console.log('[SSE_DEBUG] 解析dialogue:', fieldValue);
              currentSceneData.dialogue = fieldValue;

              // dialogue完成，分镜数据完整
              if (currentSceneData.prompt && fieldValue) {
                console.log('[SSE_DEBUG] 场景完整，更新状态，场景ID:', currentSceneId);
                updateSceneReady(currentSceneId, currentSceneData);
              }
            } else {
              // 累积同一个字段的后续内容
              if (currentSceneData.desc && !contentLine.startsWith('**')) {
                console.log('[SSE_DEBUG] 累积desc:', contentLine);
                currentSceneData.desc += '\n' + contentLine;
              } else if (currentSceneData.prompt && !contentLine.startsWith('**')) {
                console.log('[SSE_DEBUG] 累积prompt:', contentLine);
                currentSceneData.prompt += '\n' + contentLine;
              } else if (currentSceneData.dialogue && !contentLine.startsWith('**')) {
                console.log('[SSE_DEBUG] 累积dialogue:', contentLine);
                currentSceneData.dialogue += '\n' + contentLine;
              }
            }
          }
        }
      }

    } catch (error) {
      console.error('[STREAM_ERROR] 流请求失败:', error);
      stateMachine.stopGeneration();
      setSceneList([]);

      // 错误处理
      sseReaderRef.current = null;
    } finally {
      // 关闭流标记并保存分镜列表
      // 已使用状态机状态替代，无需单独设置
      // 清理reader
      sseReaderRef.current = null;

      // 确保状态机的加载状态被重置
      // 🔴 关键修复：SSE结束时将状态切换为EDITING
      // 完全使用状态机控制，如果当前状态不允许转换（比如用户已经手动停止了），
      // transitionTo会自动拒绝转换，并打印警告信息。
      stateMachine.completeGeneration();
    }
  };

  // 创建新的loading态分镜
  const createLoadingScene = () => {
    const newScene: SceneItem = {
      id: generateUniqueId(),
      desc: '',
      prompt: '',
      dialogue: '',
      images: [],
      videoUrl: '',
      audioUrl: '',
      status: SceneStatus.LOADING,
      cursorPosition: { startTime: 0, endTime: 5 },
      audioPlayState: false,
      videoLoading: false
    };

    setSceneList(prev => [...prev, newScene]);
    return newScene.id;
  };

  // 更新分镜为就绪状态
  const updateSceneReady = (sceneId: string, sceneData: Partial<SceneItem>) => {
    console.log('[SCENE_UPDATE] ✨ Updating scene to ready:', sceneId);
    console.log('[SCENE_UPDATE] 📦 Scene data to update:', sceneData);

    setSceneList(prev => {
      return prev.map(item => {
        if (item.id === sceneId) {
          // 更新为就绪状态并合并数据
          const updatedScene = { ...item, ...sceneData, status: SceneStatus.READY };
          // 生成媒体
          generateMediaForScene(sceneId, updatedScene, 'updateSceneReady');
          return updatedScene;
        }
        return item;
      });
    });
  };

  // 设置分镜为生成媒体状态
  const setSceneGeneratingMedia = (sceneId: string, isImageGenerating: boolean = false) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId
        ? { ...item, status: SceneStatus.GENERATING_MEDIA, isImageGenerating }
        : item
    ));
  };

  // 更新媒体生成结果
  const updateSceneMediaResult = (sceneId: string, mediaData: { images?: string[], audioUrl?: string, audioDuration?: number }) => {
    console.log('🔄 [updateSceneMediaResult] 开始执行', {
      sceneId,
      currentSceneId,
      callStack: new Error().stack?.split('\n').slice(1, 5).join('\n')
    });

    console.log('📦 [updateSceneMediaResult] 输入参数', {
      mediaData,
      hasImages: !!mediaData.images,
      imagesLength: mediaData.images?.length || 0,
      hasAudioUrl: !!mediaData.audioUrl,
      audioDuration: mediaData.audioDuration,
      isCurrentScene: currentSceneId === sceneId
    });

    // 1. 准备更新数据
    const updatedSceneData: Partial<SceneItem> = { status: SceneStatus.READY };

    if (mediaData.images) {
      console.log('🖼️ [updateSceneMediaResult] 处理图片数据', {
        images: mediaData.images,
        imagesLength: mediaData.images.length
      });

      if (mediaData.images.length > 0) {
        updatedSceneData.images = mediaData.images;
        updatedSceneData.isImageGenerating = false; // 图片生成完成
        console.log('✅ [updateSceneMediaResult] 图片数据有效，准备更新');
      } else {
        console.log('⚠️ [updateSceneMediaResult] 图片数组为空，不更新');
      }
    } else {
      console.log('⚠️ [updateSceneMediaResult] 没有图片数据');
    }

    if (mediaData.audioUrl) {
      console.log('🎵 [updateSceneMediaResult] 处理音频数据', {
        audioUrl: mediaData.audioUrl,
        audioDuration: mediaData.audioDuration
      });
      updatedSceneData.audioUrl = mediaData.audioUrl;

      // 如果有音频时长，更新endTime
      if (mediaData.audioDuration && mediaData.audioDuration > 0) {
        // 音频时长单位是毫秒，转换为秒
        const durationInSeconds = mediaData.audioDuration / 1000;
        // 限制在0-20秒范围内
        const clampedDuration = Math.max(0, Math.min(20, durationInSeconds));

        // 更新cursorPosition.endTime
        updatedSceneData.cursorPosition = {
          startTime: 0, // 始终为0
          endTime: clampedDuration
        };

        // 同时更新latestCursorPositionsRef
        const newCursorPosition = {
          startTime: 0,
          endTime: clampedDuration
        };
        latestCursorPositionsRef.current[sceneId] = newCursorPosition;

        console.log('⏱️ [updateSceneMediaResult] 根据音频时长更新endTime', {
          sceneId,
          audioDuration: mediaData.audioDuration,
          durationInSeconds,
          clampedDuration,
          oldPosition: latestCursorPositionsRef.current[sceneId],
          newPosition: newCursorPosition
        });
      }
    } else {
      console.log('⚠️ [updateSceneMediaResult] 没有音频数据');
    }

    console.log('📋 [updateSceneMediaResult] 准备更新的场景数据', {
      sceneId,
      updatedData: updatedSceneData
    });

    // 2. 更新场景列表
    setSceneList(prev => {
      const newSceneList = prev.map(item => {
        if (item.id === sceneId) {
          const updatedItem = { ...item, ...updatedSceneData };
          console.log('🔄 [updateSceneMediaResult] 更新场景', {
            sceneId,
            oldImages: item.images,
            newImages: updatedItem.images,
            oldStatus: item.status,
            newStatus: updatedItem.status,
            oldCursorPosition: item.cursorPosition,
            newCursorPosition: updatedItem.cursorPosition
          });
          return updatedItem;
        }
        return item;
      });
      return newSceneList;
    });

    // 3. 更新对话框图片
    if (mediaData.images && mediaData.images.length > 0) {
      console.log('🖼️ [updateSceneMediaResult] 更新dialogImages', {
        sceneId,
        imagesLength: mediaData.images.length,
        firstImage: mediaData.images[0]
      });
      setDialogImages(mediaData.images);
    } else {
      console.log('⚠️ [updateSceneMediaResult] 不更新dialogImages，图片数据无效');
    }

    console.log('✅ [updateSceneMediaResult] 执行完成');
  };

  // 为指定场景生成媒体
  const generateMediaForScene = (sceneId: string, scene: SceneItem, callLocation: string) => {
    console.log(`🎬 [generateMediaForScene] 开始生成媒体:`, { sceneId, callLocation });

    // 设置为生成中状态
    setSceneGeneratingMedia(sceneId);

    // 1. 生成图片
    if (scene.prompt && scene.prompt.trim() && scene.images.length === 0) {
      const imageRequestParams = {
        prompt: scene.prompt,
        image_size: resolution.replace(/\s*\([^)]+\)$/, ''), // 移除括号里的比例信息
        num_images: 1,
        quality: quality,
        style: 'realistic',
        watermark: false
      };

      generateImage(imageRequestParams)
        .then(response => {
          if (response.code === 200 && response.data?.image_urls?.length > 0) {
            updateSceneMediaResult(sceneId, { images: response.data.image_urls });

          }
        })
        .catch(error => {
          console.error(`❌ [${callLocation}] 图片生成失败:`, error);
          // 生图失败时重置状态为READY，保持原有图片
          setSceneList(prev => prev.map(item =>
            item.id === sceneId ? { ...item, status: SceneStatus.READY } : item
          ));
        });
    }

    // 2. 生成语音
    if (scene.dialogue && scene.dialogue.trim() && scene.audioUrl === '') {
      const ttsRequestParams = {
        text: scene.dialogue,
        voice_name: selectedVoiceType,
        audio_format: 'mp3' as const,
        volume: 1.0,
        speed: voiceSpeed,
        emotion: 'neutral' as const,
        response_type: 'url' as const
      };

      generateTTS(ttsRequestParams)
        .then(response => {
          if (response.code === 200) {
            let audioUrl = response.data?.audio_url;
            let audioDuration = 0; // 音频时长（毫秒）

            if (!audioUrl && response.data?.audio_base64) {
              try {
                const binaryString = atob(response.data.audio_base64);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'audio/mp3' });
                audioUrl = URL.createObjectURL(blob);
              } catch (error) {
                console.error('❌ base64音频解析失败:', error);
              }
            }

            // 获取音频时长并回填到endTime
            audioDuration = response.data?.duration || 0;

            if (audioUrl) {
              updateSceneMediaResult(sceneId, { audioUrl, audioDuration });
            }
          }
        })
        .catch(error => {
          console.error(`❌ [${callLocation}] 语音生成失败:`, error);
        });
    }
  };

  // 从历史项目加载数据
  const loadHistoryProject = async () => {
    if (!projectId) {
      console.error('[HISTORY_ERROR] 项目ID不存在');
      return;
    }

    try {
      setIsLoading(true);
      console.log('[HISTORY_LOAD] 📂 开始加载历史项目，ID:', projectId);

      // 调用API 获取历史项目数据
      const projectConfig = await getHistoryProject(projectId);

      console.log('[HISTORY_LOAD] � 完整API响应:', projectConfig);
      console.log('[HISTORY_LOAD] 📊 API响应code:', projectConfig.code);
      console.log('[HISTORY_LOAD] 💬 API响应message:', projectConfig.message);
      console.log('[HISTORY_LOAD] 📦 API响应data:', projectConfig.data);

      // 检查API响应格式
      if (projectConfig.code !== 200) {
        throw new Error(`API返回错误: ${projectConfig.message}`);
      }

      // 从历史数据中加载标题
      const projectData = projectConfig.data;
      if (!projectData) {
        throw new Error('API返回数据为空');
      }

      if (projectData.title) {
        setTitle(projectData.title);
        titleRef.current = projectData.title; // 立即保存到ref
        console.log('[HISTORY_LOAD] 📝 加载标题:', projectData.title);
      }

      // 更新场景列表 - 注意：API返回的分镜列表在project_config字段中，兼容新旧两种格式
      let sceneList: any[];
      let bgMusicData: { url: string | null; volume: number } | null = null;
      let voiceSpeedData: number | null = null;

      // 检查project_config的格式
      if (Array.isArray(projectData.project_config)) {
        // 旧格式：project_config是直接的场景列表数组
        console.log('[HISTORY_LOAD] 📋 检测到旧格式project_config（数组）');
        sceneList = projectData.project_config;
      } else if (typeof projectData.project_config === 'object' && projectData.project_config !== null) {
        // 新格式：project_config是包含bgMusic、voiceSpeed、sceneList和subtitleStyle的对象
        console.log('[HISTORY_LOAD] 📋 检测到新格式project_config（对象）');
        sceneList = projectData.project_config.sceneList || [];
        // 提取背景音乐数据
        bgMusicData = projectData.project_config.bgMusic;
        if (bgMusicData) {
          console.log('[HISTORY_LOAD] 🎵 加载背景音乐数据:', bgMusicData);
        }
        // 提取语速数据
        voiceSpeedData = projectData.project_config.voiceSpeed;
        if (voiceSpeedData !== null && voiceSpeedData !== undefined) {
          console.log('[HISTORY_LOAD] 🎚️ 加载语速数据:', voiceSpeedData);
        }
        // 提取字幕样式数据（新增）
        const subtitleStyleData = projectData.project_config.subtitleStyle;
        if (subtitleStyleData) {
          console.log('[HISTORY_LOAD] 📝 加载字幕样式数据:', subtitleStyleData);
          // 直接设置subtitleStyle状态
          _setSubtitleStyle(subtitleStyleData);
        }
      } else {
        throw new Error('project_config格式无效');
      }

      console.log('[HISTORY_LOAD] 🎬 原始sceneList:', sceneList);

      // 转换旧格式转场配置的函数
      const convertOldTransitionFormat = (oldTransition: any) => {
        if (!oldTransition) return undefined;

        const newTransition: TransitionConfig = {
          type: oldTransition.type,
          duration: oldTransition.duration,
          easing: oldTransition.easing || 'inout'
        };

        // 处理合并的转场类型（如slide_left）
        if (newTransition.type.includes('_')) {
          const parts = newTransition.type.split('_');
          newTransition.type = parts[0];
          newTransition.direction = parts[1];
        }

        // 处理毫秒时长
        if (newTransition.duration > 10) {
          // 假设大于10的是毫秒值
          newTransition.duration = parseFloat((newTransition.duration / 1000).toFixed(2));
        }

        // 处理缓动函数格式
        const easingMap: { [key: string]: string } = {
          'ease': 'inout',
          'linear': 'linear',
          'ease-in': 'in',
          'ease-out': 'out',
          'ease-in-out': 'inout'
        };
        if (easingMap[newTransition.easing as string]) {
          newTransition.easing = easingMap[newTransition.easing as string];
        }

        return newTransition;
      };

      const transformedScenes = sceneList.map((scene: any) => {
        // 转换旧格式转场配置
        const transformedScene = {
          ...scene,
          status: SceneStatus.READY,
          cursorPosition: scene.cursorPosition || { startTime: 0, endTime: 5 },
          audioPlayState: false,
          videoLoading: false
        };

        // 转换转场配置
        if (transformedScene.transition) {
          transformedScene.transition = convertOldTransitionFormat(transformedScene.transition);
        }

        return transformedScene;
      });

      console.log('[HISTORY_LOAD] ✨ 转换后的sceneList:', transformedScenes);
      console.log('🎬 [VIDEOEDIT] 准备调用setSceneList，场景数量:', transformedScenes.length);
      console.log('🎬 [VIDEOEDIT] 播放器容器状态:', {
        container: videoPlayerContainer.current,
        hasContainer: !!videoPlayerContainer.current,
        playerInstance: videoPlayerInstance.current,
        hasPlayerInstance: !!videoPlayerInstance.current
      });

      setSceneList(transformedScenes);

      console.log('🎬 [VIDEOEDIT] setSceneList调用完成');

      // 加载背景音乐数据
      if (bgMusicData) {
        // 设置选中的背景音乐
        if (bgMusicData.url) {
          console.log('[HISTORY_LOAD] 🎵 设置选中的背景音乐:', bgMusicData.url);
          setSelectedBgMusic(bgMusicData.url);
        }
        // 设置背景音乐音量
        if (typeof bgMusicData.volume === 'number') {
          console.log('[HISTORY_LOAD] 🎚️ 设置背景音乐音量:', bgMusicData.volume);
          setBgMusicVolume(bgMusicData.volume);
        }
      }

      // 加载语速数据
      if (voiceSpeedData !== null && voiceSpeedData !== undefined) {
        console.log('[HISTORY_LOAD] 🎚️ 设置语速:', voiceSpeedData);
        setVoiceSpeed(voiceSpeedData);
      }

      // 🔧 关键修复：设置当前项目ID，确保后续保存时使用同一个ID
      resetCurrentProjectId(projectId);
      console.log('[HISTORY_LOAD] 🆔 设置当前项目ID:', projectId);

      // 🔧 方案五：设置历史加载完成标记
      setHistoryLoaded(true);
      console.log('🎬 [VIDEOEDIT] 历史加载完成标记已设置');

      // 🔧 关键修复：初始化lastSavedDataRef，避免加载后立即触发保存
      // 注意：由于voiceSpeed是异步设置的，这里使用voiceSpeedData的值
      lastSavedDataRef.current = {
        title: projectData.title || t('videoEdit.untitledVideo'),
        bgMusic: bgMusicData || { url: null, volume: 0.5 },
        voiceSpeed: voiceSpeedData !== null && voiceSpeedData !== undefined ? voiceSpeedData : 1.0,
        sceneList: sceneList
      };
      console.log('[HISTORY_LOAD] 📦 初始化lastSavedDataRef:', lastSavedDataRef.current);

    } catch (error) {
      console.error('[HISTORY_ERROR] ❌ 历史项目加载失败:', error);
      // 显示错误提示
      setToastType('error');
      setToastVisible(true);
      setToastMessage(t('toast.video.loadHistoryFailed', { error: error instanceof Error ? error.message : t('toast.common.unknownError') }));
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== 保存功能 ====================

  // 保存分镜列表 - 支持传入最新参数，避免闭包陷阱
  const saveSceneList = async (
    latestTitle?: string,
    sceneListParam?: SceneItem[],
    bgMusicParam?: { url: string | null; volume: number } | null,
    voiceSpeedParam?: number,
    subtitleStyleParam?: SubtitleStyle  // 新增：字幕样式参数
  ) => {
    console.log('[AutoSave] saveSceneList调用开始');
    // 🔧 优先使用传入的参数，否则使用当前state值（解决闭包陷阱）
    const currentSceneList = sceneListParam || sceneList;
    const currentBgMusicUrl = bgMusicParam !== undefined ? bgMusicParam.url : selectedBgMusic;
    const currentBgMusicVolume = bgMusicParam !== undefined ? bgMusicParam.volume : bgMusicVolume;
    const currentVoiceSpeed = voiceSpeedParam !== undefined ? voiceSpeedParam : voiceSpeed;
    const currentSubtitleStyle = subtitleStyleParam !== undefined ? subtitleStyleParam : subtitleStyle;  // 新增
    if (!currentSceneList.length) {
      console.log('[AutoSave] 场景列表为空，跳过保存');
      return;
    }

    // 🔧 关键修复：合并sceneList和latestCursorPositionsRef中的最新游标位置
    const mergedSceneList = currentSceneList.map(scene => {
      const latestCursorPosition = latestCursorPositionsRef.current[scene.id];
      if (latestCursorPosition) {
        console.log('[AutoSave] 合并最新游标位置:', {
          sceneId: scene.id,
          oldPosition: scene.cursorPosition,
          newPosition: latestCursorPosition
        });
        return {
          ...scene,
          cursorPosition: latestCursorPosition
        };
      }
      return scene;
    });

    // 过滤掉不需要保存的字段
    const filteredSceneList = mergedSceneList.map(scene => {
      const { audioPlayState, videoLoading, ...rest } = scene;
      return rest;
    });

    // 在保存的JSON外层增加title字段
    const finalTitle = latestTitle || titleRef.current || title || t('videoEdit.untitledVideo');
    console.log('[TITLE_DEBUG] saveSceneList调用，latestTitle:', latestTitle, 'titleRef:', titleRef.current, '当前title:', title, '最终使用:', finalTitle);
    const savedData = {
      title: finalTitle,
      bgMusic: {
        url: currentBgMusicUrl,
        volume: currentBgMusicVolume
      },
      voiceSpeed: currentVoiceSpeed,
      sceneList: filteredSceneList,
      subtitleStyle: currentSubtitleStyle  // 新增：字幕样式
    };
    console.log('[TITLE_DEBUG] 生成的savedData:', savedData);

    // ========== 新增：数据diff和时间判定 ==========
    // 1. 计算数据diff - 比较完整的savedData（包含title、bgMusic、sceneList）
    const hasDiff = JSON.stringify(savedData) !== JSON.stringify(lastSavedDataRef.current);

    // 2. 检查时间间隔 - 至少1秒
    const now = Date.now();
    const timeDiff = now - lastSaveTimeRef.current;
    const isTimeValid = timeDiff > 1000;

    // 3. 如果没有diff或时间较短，不再处理
    if (!hasDiff) {
      console.log('[AutoSave] 数据无变化，跳过保存');
      console.log('[AutoSave] 当前savedData:', savedData);
      console.log('[AutoSave] 上次保存的savedData:', lastSavedDataRef.current);
      return;
    }

    if (!isTimeValid) {
      console.log('[AutoSave] 时间间隔过短，跳过保存');
      return;
    }

    // 更新上次保存的时间和数据
    lastSaveTimeRef.current = now;
    lastSavedDataRef.current = savedData;
    console.log('[AutoSave] 数据有变化且时间间隔有效，执行保存');
    // ========== 数据diff和时间判定结束 ==========

    const jsonData = JSON.stringify(savedData, null, 2);
    console.log('[AutoSave] 保存触发 - 包含title的完整数据:');
    console.log(jsonData);

    // ==== 3秒延时频控调用API保存到服务器 ====
    // 清除之前的定时器
    if (apiSaveTimerRef.current) {
      clearTimeout(apiSaveTimerRef.current);
      console.log('[AutoSave] 清除之前的保存定时器');
    }

    // 设置新的3秒延时保存
    apiSaveTimerRef.current = setTimeout(async () => {
      try {
        // 调用API保存到服务器
        console.log('[AutoSave] 调用API保存到服务器...');
        // 确保savedData包含title字段
        if (!savedData.title) {
          savedData.title = t('videoEdit.untitledVideo');
          console.log('[TITLE_DEBUG] 确保title存在:', savedData.title);
        }
        await saveSceneListApi(savedData);
        console.log('[AutoSave] API保存成功');
      } catch (error) {
        console.error('[AutoSave] API调用失败:', error);
      }
    }, 6000); // 6秒延时

    // ==== 保存后同步更新播放器 ====
    try {
      console.log('🎬 [VIDEOEDIT] 保存后同步更新播放器开始:', {
        hasContainer: !!videoPlayerContainer.current,
        containerElement: videoPlayerContainer.current,
        sceneListLength: mergedSceneList.length,
        hasPlayerInstance: !!videoPlayerInstance.current,
        currentSceneId,
        projectId
      });

      if (videoPlayerContainer.current && mergedSceneList.length > 0) {
        // 找到当前场景的索引
        let startSceneIndex = 0;
        if (currentSceneId) {
          startSceneIndex = mergedSceneList.findIndex(scene => scene.id === currentSceneId);
          // 如果没找到，默认从0开始
          if (startSceneIndex === -1) {
            startSceneIndex = 0;
          }
        }

        // 获取当前播放器状态
        const currentPlaybackStatus = videoPlayerInstance.current?.isPlaying || false;
        console.log('🎬 [VIDEOEDIT] 保存后同步更新播放器，当前场景:', startSceneIndex, '播放状态:', currentPlaybackStatus);

        // 详细场景数据检查
        console.log('🎬 [VIDEOEDIT] 场景数据检查:', {
          sceneCount: mergedSceneList.length,
          firstScene: mergedSceneList[0] ? {
            id: mergedSceneList[0].id,
            hasVideo: !!(mergedSceneList[0].videoUrl),
            hasImages: !!(mergedSceneList[0].images && mergedSceneList[0].images.length > 0),
            status: mergedSceneList[0].status,
            cursorPosition: mergedSceneList[0].cursorPosition
          } : null
        });

        // 更新或初始化播放器 - 使用合并后的数据，确保包含最新的游标位置
        if (videoPlayerInstance.current) {
          console.log('🎬 [VIDEOEDIT] 更新现有播放器实例');
          videoPlayerInstance.current = videoPlayerInstance.current.reloadToScene(
            mergedSceneList,
            startSceneIndex,
            currentPlaybackStatus
          );
        } else {
          // 初始化播放器
          console.log('🎬 [VIDEOEDIT] 初始化新的播放器实例');
          console.log('🎬 [VIDEOEDIT] 播放器参数:', {
            container: videoPlayerContainer.current,
            containerId: videoPlayerContainer.current.id,
            containerClassName: videoPlayerContainer.current.className,
            sceneList: mergedSceneList
          });

          try {
            videoPlayerInstance.current = new PureFrontendVideoPlayer(
              videoPlayerContainer.current,
              mergedSceneList,
              false,
              currentBgMusicUrl,
              currentBgMusicVolume,
              undefined,
              undefined,
              subtitleStyle  // ← 新增：传递字幕样式
            );
            console.log('🟢 [SUBTITLE_DEBUG] 播放器初始化成功');
            console.log('🟢 [SUBTITLE_DEBUG] 传递给播放器的subtitleStyle:', subtitleStyle);

            // 🔧 关键：初始化后主动应用字幕样式（与历史记录路径保持一致）
            if (subtitleStyle && subtitleStyle.templateId && subtitleStyle.templateId !== 'default') {
              console.log('🟢 [SUBTITLE_DEBUG] 立即应用字幕样式');
              console.log('🟢 [SUBTITLE_DEBUG] 样式内容:', JSON.stringify(subtitleStyle, null, 2));
              videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
              console.log('🟢 [SUBTITLE_DEBUG] ✓ 已调用updateSubtitleStyle');
            } else {
              console.log('🟢 [SUBTITLE_DEBUG] 不需要应用字幕样式');
              console.log('🟢 [SUBTITLE_DEBUG]   - subtitleStyle存在:', !!subtitleStyle);
              console.log('🟢 [SUBTITLE_DEBUG]   - templateId:', subtitleStyle?.templateId);
            }
          } catch (playerError) {
            console.error('🎬 [VIDEOEDIT] 播放器构造失败:', playerError);
            throw playerError;
          }
        }
      } else {
        console.log('🎬 [VIDEOEDIT] 跳过播放器更新:', {
          hasContainer: !!videoPlayerContainer.current,
          sceneListLength: mergedSceneList.length,
          reason: !videoPlayerContainer.current ? '播放器容器不存在' : '场景列表为空'
        });
      }
    } catch (error) {
      console.error('🎬 [VIDEOEDIT] 更新播放器失败:', error);
      console.error('🎬 [VIDEOEDIT] 错误堆栈:', error.stack);
    }

    console.log('[AutoSave] saveSceneList调用结束');
  };

  // 播放器相关功能 - 重载并从指定场景开始播放
  const reloadPlayerAndPlayFromScene = (sceneIndex: number = 0) => {
    console.log('[PLAYER] 🔄 重新加载播放器并从场景', sceneIndex, '开始播放');

    // 检查播放器容器是否存在
    if (videoPlayerContainer.current) {
      if (videoPlayerInstance.current) {
        console.log('[PLAYER] 🗑️ 销毁旧播放器实例');
        videoPlayerInstance.current.destroy();
        videoPlayerInstance.current = null;
      }

      console.log('[PLAYER] 🚀 创建新播放器实例');
      console.log('[PLAYER] 📋 当前场景列表:', sceneList);

      // 创建新的播放器实例 - 注意：第二个参数是sceneList数组，不是配置对象
      videoPlayerInstance.current = new PureFrontendVideoPlayer(
        videoPlayerContainer.current,
        sceneList,
        false,
        selectedBgMusic,
        bgMusicVolume,
        undefined,
        undefined,
        subtitleStyle  // ← 传递字幕样式
      );

      // 初始化后立即应用字幕样式
      if (subtitleStyle) {
        console.log('🟢 [SUBTITLE_DEBUG] 在handlePlayFromScene中应用字幕样式');
        videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
      }

      // 从指定场景开始播放
      if (sceneIndex >= 0 && sceneIndex < sceneList.length) {
        console.log('[PLAYER] ⏩ 从场景', sceneIndex, '开始播放');
        videoPlayerInstance.current.playFromScene(sceneIndex);
      } else {
        // 开始播放第一个场景
        videoPlayerInstance.current.play();
      }
    }
  };

  // ==================== 对话框处理函数 ====================

  // 实时更新提示词（用于弹窗编辑时的实时同步）
  const handleUpdatePromptSilent = (sceneId: string, newPrompt: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, prompt: newPrompt } : item
    ));
  };

  const handleUpdatePrompt = (sceneId: string, newPrompt: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, prompt: newPrompt } : item
    ));

  };

  // 实时更新旁白（用于弹窗编辑时的实时同步）
  const handleUpdateNarrationSilent = (sceneId: string, newNarration: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, dialogue: newNarration } : item
    ));
  };

  const handleUpdateNarration = (sceneId: string, newNarration: string) => {
    setSceneList(prev => prev.map(item =>
      item.id === sceneId ? { ...item, dialogue: newNarration, audioUrl: '' } : item
    ));

  };

  const handleUpdateImages = (sceneId: string, images: string[]) => {
    console.log('🖼️ [handleUpdateImages] 开始执行', {
      sceneId,
      currentSceneId,
      images,
      imagesLength: images.length,
      callStack: new Error().stack?.split('\n').slice(1, 5).join('\n')
    });

    // 直接更新场景图片
    setSceneList(prev => {
      const newSceneList = prev.map(item => {
        if (item.id === sceneId) {
          const updatedItem = { ...item, images, status: SceneStatus.READY, isImageGenerating: false };
          console.log('🔄 [handleUpdateImages] 更新场景图片', {
            sceneId,
            oldImages: item.images,
            newImages: updatedItem.images,
            oldStatus: item.status,
            newStatus: updatedItem.status
          });
          return updatedItem;
        }
        return item;
      });
      return newSceneList;
    });

    // 更新dialogImages状态，确保对话框中显示最新的图片
    console.log('🖼️ [handleUpdateImages] 更新dialogImages', {
      sceneId,
      currentSceneId,
      isCurrentScene: currentSceneId === sceneId
    });
    setDialogImages(images);

    console.log('💾 [handleUpdateImages] 触发保存场景列表');


    console.log('✅ [handleUpdateImages] 执行完成');
  };

  // ==================== 视频合成功能 ====================

  const handleVideoComposition = async () => {
    console.log('🎬 [handleVideoComposition] 开始处理视频合成');

    // 检查是否有场景数据
    if (!sceneList.length) {
      console.warn('⚠️ [handleVideoComposition] 场景列表为空，无法合成视频');
      alert(t('videoEdit.noSceneDataError'));
      return;
    }

    // 检查是否有标题
    if (!title.trim()) {
      console.warn('⚠️ [handleVideoComposition] 视频标题为空');
      alert(t('videoEdit.noTitleError'));
      return;
    }

    // 检查是否存在images和videoUrl都为空的场景
    const hasEmptyScene = sceneList.some(scene => {
      const hasNoImages = !scene.images || scene.images.length === 0;
      const hasNoVideo = !scene.videoUrl || scene.videoUrl === '';
      return hasNoImages && hasNoVideo;
    });

    if (hasEmptyScene) {
      console.warn('⚠️ [handleVideoComposition] 存在空白帧或缺失的分镜');
      setToastMessage(t('videoEdit.emptyFrameError'));
      setToastType('error');
      setToastVisible(true);
      return;
    }

    try {
      // 🔧 关键修复：合并sceneList和latestCursorPositionsRef中的最新游标位置
      const mergedSceneList = sceneList.map(scene => {
        const latestCursorPosition = latestCursorPositionsRef.current[scene.id];
        if (latestCursorPosition) {
          console.log('🔧 [handleVideoComposition] 合并最新游标位置:', {
            sceneId: scene.id,
            oldPosition: scene.cursorPosition,
            newPosition: latestCursorPosition
          });
          return {
            ...scene,
            cursorPosition: latestCursorPosition
          };
        }
        return scene;
      });

      // 转场配置验证和处理函数
      const validateTransition = (transition: any) => {
        if (!transition || !transition.type) {
          return null;
        }

        // 转场类型与方向的映射关系
        const TRANSITION_DIRECTION_MAP: { [key: string]: string[] } = {
          fade: [],
          dissolve: [],
          wipe: ['left', 'right', 'up', 'down'],
          slide: ['left', 'right', 'up', 'down'],
          push: ['left', 'right', 'up', 'down'],
          circlecrop: [],
          rectcrop: []
        };

        // 支持的转场类型
        const validTypes = Object.keys(TRANSITION_DIRECTION_MAP);
        // 支持的缓动函数
        const validEasings = ['in', 'out', 'inout', 'sine', 'cubic', 'quint', 'circ'];

        // 验证转场类型
        const validType = validTypes.includes(transition.type);
        if (!validType) {
          transition.type = 'fade'; // 使用默认类型
        }

        // 检查方向是否有效
        const supportedDirections = TRANSITION_DIRECTION_MAP[transition.type];
        if (supportedDirections.length > 0) {
          if (!transition.direction || !supportedDirections.includes(transition.direction)) {
            transition.direction = 'right'; // 使用默认方向
          }
        } else {
          delete transition.direction; // 移除无效方向
        }

        // 检查持续时间
        if (typeof transition.duration !== "number" || transition.duration <= 0 || transition.duration > 3.0) {
          transition.duration = 0.5; // 使用默认值
        }

        // 检查缓动函数
        if (!transition.easing || !validEasings.includes(transition.easing)) {
          transition.easing = 'inout'; // 使用默认值
        }

        return transition;
      };

      // 过滤掉不需要提交的字段，准备API数据
      const apiSceneList = mergedSceneList.map((scene, index) => {
        const {
          audioPlayState,
          videoLoading,
          status,
          ...apiScene
        } = scene;

        // 转场设置规则：
        // 1. 尾帧不能设置转场（最后一个场景，不管是否唯一）
        // 2. 首帧如果是唯一的场景，不能设置转场
        // 3. 首帧如果不是唯一的场景，可以设置转场
        if (index === mergedSceneList.length - 1) {
          delete apiScene.transition;
        } else if (index === 0 && mergedSceneList.length === 1) {
          delete apiScene.transition;
        } else if (apiScene.transition) {
          // 验证并修复转场配置
          apiScene.transition = validateTransition(apiScene.transition);
          // 如果验证后转场配置无效，移除它
          if (!apiScene.transition) {
            delete apiScene.transition;
          }
        }

        return {
          ...apiScene,
          status: 'ready', // API要求status为ready
          cursorPosition: apiScene.cursorPosition || { startTime: 0, endTime: 5 } // 确保cursorPosition始终有值
        };
      });

      // 准备提交给API的数据
      const videoTaskData = {
        title: title.trim(),
        bgMusic: {
          url: selectedBgMusic,
          volume: bgMusicVolume
        },
        sceneList: apiSceneList,
        subtitleStyle: subtitleStyle // 添加字幕样式配置
      };

      // 📋 打印提交参数信息
      console.log('📋 [handleVideoComposition] 提交的视频合成参数:');
      console.log('标题:', videoTaskData.title);
      console.log('场景数量:', videoTaskData.sceneList.length);
      console.log('完整JSON数据:');
      console.log(JSON.stringify(videoTaskData, null, 2));

      // 显示提交中的提示
      const message = `${t('toast.common.processing')}\n${t('videoEdit.title')}: ${videoTaskData.title}\n${t('videoEdit.sceneCount', { count: videoTaskData.sceneList.length })}`;
      setToastMessage(message);
      setToastType('success');
      setToastVisible(true);

      // 调用API提交视频合成任务
      const response = await createVideoTask(videoTaskData);

      if (response.code === 200 && response.data?.taskId) {
        const successMessage = `${t('videoEdit.taskSubmittedSuccessWithEmail')}\n${t('videoEdit.title')}: ${videoTaskData.title}`;
        setToastMessage(successMessage);
        setToastType('success');
        setToastVisible(true);
        console.log('✅ [handleVideoComposition] 视频合成任务提交成功:', {
          taskId: response.data.taskId,
          title: videoTaskData.title
        });
      } else {
        throw new Error(response.message || t('videoEdit.taskSubmittedError'));
      }

    } catch (error) {
      console.error('❌ [handleVideoComposition] 视频合成任务提交失败:', error);
      const errorMessage = `${t('toast.common.error')} ${t('videoEdit.taskSubmittedError')}: ${error instanceof Error ? error.message : t('toast.common.unknownError')}`;
      setToastMessage(errorMessage);
      setToastType('error');
      setToastVisible(true);
    }
  };

  // 视频相关处理函数
  const handleUpdateVideo = (sceneId: string, videoUrl: string, durationFromTask?: number) => {
    setSceneList(prev => prev.map(scene =>
      scene.id === sceneId ? { ...scene, videoUrl } : scene
    ));

    // 获取并保存视频时长 - 如果任务中有时长信息，直接使用它，否则从视频文件中获取
    const updateVideoDuration = async () => {
      try {
        let duration;
        let updatedList: SceneItem[];

        if (durationFromTask) {
          duration = durationFromTask;
          console.log(`从任务参数获取视频时长: ${duration}秒`);
        } else {
          duration = await getVideoDuration(videoUrl);
          console.log(`从视频文件获取视频时长: ${duration}秒`);
        }

        // 更新场景的视频时长
        setSceneList(prev => {
          updatedList = prev.map(scene =>
            scene.id === sceneId ? { ...scene, videoDuration: duration } : scene
          );
          return updatedList;
        });

        // 保存场景列表


        // 根据时长设置右侧游标位置
        const updateCursorPosition = () => {
          setSceneList(prev => {
            // 创建一个新的数组来保存更新后的场景
            updatedList = [...prev];

            // 查找当前场景的索引
            const sceneIndex = updatedList.findIndex(scene => scene.id === sceneId);

            if (sceneIndex !== -1 && duration) {
              // 设置游标位置 - startTime保持不变，endTime设置为视频时长
              const newCursorPosition: CursorPosition = {
                startTime: updatedList[sceneIndex].cursorPosition?.startTime || 0,
                endTime: Math.min(duration, 20) // 限制在0-20s范围内，根据系统设计调整
              };

              // 更新场景的cursorPosition
              updatedList[sceneIndex] = {
                ...updatedList[sceneIndex],
                cursorPosition: newCursorPosition
              };

              // 同时更新latestCursorPositionsRef - 确保与状态同步
              latestCursorPositionsRef.current[sceneId] = newCursorPosition;
            }

            return updatedList;
          });
        };

        // 立即更新游标位置
        updateCursorPosition();

        // 更新tooltip内容
        if (duration) {
          const roundedValue = Math.max(0, Math.min(20, Number(duration.toFixed(2))));
          // 查找对应的场景卡片
          const sceneCards = document.querySelectorAll('.w-full.min-w-\[500px\].max-w-\[700px\].flex.flex-col.flex-shrink-0');
          sceneCards.forEach((card, cardIndex) => {
            // 查找当前场景的索引
            const sceneIndex = updatedList?.findIndex((scene: SceneItem) => scene.id === sceneId);
            if (cardIndex === sceneIndex) {
              // 查找结束游标元素
              const endCursor = card.querySelector('.bg-purple-500\/70.hover\:bg-purple-500\/90');
              if (endCursor) {
                // 查找tooltip元素
                const tooltip = endCursor.querySelector('div:last-child');
                if (tooltip) {
                  tooltip.textContent = `${roundedValue.toFixed(1)}s`;
                }
              }
            }
          });
        }

      } catch (error) {
        console.error('获取视频时长失败:', error);
      }
    };

    // 调用获取视频时长的函数
    updateVideoDuration();

    // 视频生成成功后保存场景列表

  };

  const handleUpdateVideoTaskId = (sceneId: string, taskId: string) => {
    setSceneList(prev => prev.map(scene =>
      scene.id === sceneId ? { ...scene, videoTaskId: taskId } : scene
    ));
    // 更新视频任务ID后保存场景列表

  };

  const handleUpdateVideoStatus = (sceneId: string, status: string) => {
    setSceneList(prev => prev.map(scene =>
      scene.id === sceneId ? { ...scene, videoStatus: status } : scene
    ));
    // 仅当视频状态是最终状态（成功/失败）时才保存场景列表
    // 避免在轮询过程中（pending/processing状态）频繁调用保存接口
    if (status === 'succeeded' || status === 'failed') {

    }
  };

  // 注册VideoService任务状态变化回调
  useEffect(() => {
    const handleTaskStatusChange = (task: any) => {
      console.log('任务状态变化:', task);

      // 更新视频状态
      handleUpdateVideoStatus(task.sceneId, task.status);

      // 如果任务成功，更新视频URL
      if (task.status === 'succeeded' && task.videoUrl) {
        // 更新视频URL，同时传递任务中的时长信息（如果有）
        handleUpdateVideo(task.sceneId, task.videoUrl, task.duration);

        // 显示成功提示
        setToastMessage(t('toast.video.generationSuccess'));
        setToastType('success');
        setToastVisible(true);
      } else if (task.status === 'failed') {
        // 显示失败提示
        setToastMessage(t('toast.video.generationFailed', { error: task.error || t('toast.common.unknownError') }));
        setToastType('error');
        setToastVisible(true);
      }
    };

    // 注册回调
    videoService.onTaskStatusChange(handleTaskStatusChange);

    // 清理函数
    return () => {
      videoService.offTaskStatusChange(handleTaskStatusChange);
    };
  }, []);

  // 暴露ref方法给外部调用
  useImperativeHandle(ref, () => ({
    reloadPlayerAndPlayFromScene,
    getSceneList: () => sceneList
  }));

  // 标题编辑对话框的处理函数
  const handleTitleEditOpen = () => {
    setEditTitle(title);
    setIsTitleEditDialogOpen(true);
  };

  const handleTitleEditClose = () => {
    setIsTitleEditDialogOpen(false);
  };

  const handleTitleSave = () => {
    setTitle(editTitle); // 保存标题到state
    titleRef.current = editTitle; // 立即保存到ref，避免异步问题
    console.log('[TITLE_DEBUG] 编辑标题对话框关闭，设置标题:', editTitle);
    console.log('[TITLE_DEBUG] titleRef.current:', titleRef.current);
    setIsTitleEditDialogOpen(false);

  };

  // 转场设置相关函数
  const handleTransitionSettingOpen = (sceneId: string) => {
    setCurrentSceneId(sceneId);
    setIsTransitionSettingDialogOpen(true);
  };

  const handleTransitionSettingClose = () => {
    setIsTransitionSettingDialogOpen(false);
  };

  const handleSaveTransition = (sceneId: string, transition: TransitionConfig) => {
    setSceneList(prevList =>
      prevList.map(item =>
        item.id === sceneId ? { ...item, transition } : item
      )
    );
  };

  // 创建空场景卡片
  const createEmptyScene = () => {
    console.log('[VideoEditPage] 🎬 创建空场景卡片');

    // 生成唯一ID
    const newSceneId = generateUniqueId();

    // 创建空场景对象
    const newScene: SceneItem = {
      id: newSceneId,
      desc: '',
      prompt: '',
      dialogue: '',
      images: [],
      videoUrl: '',
      audioUrl: '',
      status: SceneStatus.READY,
      cursorPosition: { startTime: 0, endTime: 5 },
      audioPlayState: false,
      videoLoading: false
    };

    // 添加到场景列表
    setSceneList([newScene]);

    // 确保状态机处于编辑状态
    stateMachine.completeGeneration();
  };

  // 页面挂载时的处理逻辑
  useEffect(() => {
    console.log('[VideoEditPage] 🎬 页面挂载');

    // 检查是否是从历史记录进入
    if (projectId) {
      console.log('[VideoEditPage] 🔄 从历史记录进入，项目ID:', projectId);
      loadHistoryProject();
    }

    // 检查是否是从VideoInputPage进入并带有空脚本标记
    else if (isEmptyScript) {
      console.log('[VideoEditPage] ➡️ 从VideoInputPage进入，空脚本模式');
      // 重置当前项目ID，创建新的projectId
      resetCurrentProjectId();
      // 🔧 关键修复：重置lastSavedDataRef，确保新项目的diff判断正确
      lastSavedDataRef.current = null;
      // 创建空场景卡片
      createEmptyScene();
    }

    // 检查是否是从VideoInputPage进入并带有输入内容
    else if (inputContent) {
      console.log('[VideoEditPage] ➡️ 从VideoInputPage进入，输入内容:', inputContent);
      // 直接调用streamChatRequest
      streamChatRequest(inputContent);
    }

    // 页面卸载时的清理逻辑
    return () => {
      console.log('[VideoEditPage] 🧹 页面卸载');

      // 取消任何正在进行的流请求
      if (sseReaderRef.current) {
        console.log('[SSE] 🚫 取消正在进行的流请求');
        sseReaderRef.current.cancel();
        sseReaderRef.current = null;
      }

      // 清理播放器实例
      if (videoPlayerInstance.current) {
        console.log('[PLAYER] 🗑️ 卸载时销毁播放器实例');
        videoPlayerInstance.current.destroy();
        videoPlayerInstance.current = null;
      }
    };
  }, [projectId, inputContent, isEmptyScript]);

  // 监听sceneList和场景状态变化，初始化或更新播放器
  // 🔧 临时注掉：setSceneList()已经主动处理播放器更新，避免重复更新和竞争条件
  useEffect(() => {
    const initializeOrUpdatePlayer = () => {
      if (videoPlayerContainer.current && sceneList.length > 0) {
        console.log('🎬 [useEffect] 初始化或更新播放器实例');

        // 检查是否所有场景都已准备就绪或有任何就绪场景
        const allScenesReady = sceneList.every(scene => scene.status === SceneStatus.READY);
        const hasReadyScenes = sceneList.some(scene => scene.status === SceneStatus.READY);

        // 如果所有场景都已准备就绪，或者播放器实例不存在，或者有就绪场景且播放器已存在，就初始化或更新播放器
        if (allScenesReady || !videoPlayerInstance.current || (hasReadyScenes && videoPlayerInstance.current)) {
          if (videoPlayerInstance.current) {
            // 更新现有播放器实例
            console.log('🎬 [useEffect] 更新现有播放器实例');
            // 🔧 注掉播放器更新逻辑，由setSceneList()主动处理
            // videoPlayerInstance.current = videoPlayerInstance.current.reloadToScene(
            //   sceneList,
            //   0,
            //   false
            // );
          } else {
            // 初始化新的播放器实例
            console.log('🎬 [useEffect] 初始化新的播放器实例');
            // 🔧 注掉播放器初始化逻辑，由setSceneList()主动处理
            // videoPlayerInstance.current = new PureFrontendVideoPlayer(videoPlayerContainer.current, sceneList);
          }
        }
      }
    };

    // 🔧 注掉整个播放器初始化逻辑，避免与setSceneList()的主动更新产生冲突
    // 确保DOM渲染完成后初始化或更新
    // if (document.readyState === 'loading') {
    //   document.addEventListener('DOMContentLoaded', initializeOrUpdatePlayer);
    // } else {
    //   initializeOrUpdatePlayer();
    // }

    // return () => {
    //   document.removeEventListener('DOMContentLoaded', initializeOrUpdatePlayer);
    // };
  }, [sceneList]);

  // 🔧 方案五：监听历史加载完成和DOM就绪
  useEffect(() => {
    console.log('🎬 [VIDEOEDIT] useEffect触发:', {
      historyLoaded,
      hasContainer: !!videoPlayerContainer.current,
      sceneListLength: sceneList.length,
      hasPlayerInstance: !!videoPlayerInstance.current,
      hasSubtitleStyle: !!subtitleStyle
    });

    if (historyLoaded && videoPlayerContainer.current && sceneList.length > 0 && !videoPlayerInstance.current) {
      console.log('🎬 [VIDEOEDIT] 历史加载完成且DOM就绪，初始化播放器');
      try {
        videoPlayerInstance.current = new PureFrontendVideoPlayer(
          videoPlayerContainer.current,
          sceneList,
          false,
          selectedBgMusic,
          bgMusicVolume,
          undefined,
          undefined,
          subtitleStyle  // ← 传递字幕样式
        );
        console.log('🎬 [VIDEOEDIT] 历史记录播放器初始化成功:', !!videoPlayerInstance.current);

        // 初始化完成后，应用字幕样式（如果有）
        if (subtitleStyle) {
          console.log('🎬 [VIDEOEDIT] 应用历史记录的字幕样式:', subtitleStyle);
          videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
        }

        // 🔧 初始化完成后使用 reloadToScene 加载场景（但不自动播放）
        setTimeout(() => {
          if (videoPlayerInstance.current) {
            console.log('🎬 [VIDEOEDIT] 使用 reloadToScene 加载历史记录场景');
            videoPlayerInstance.current = videoPlayerInstance.current.reloadToScene(
              sceneList,
              0,
              false // autoPlay=false，用户手动播放
            );

            // 🔧 修复：reloadToScene 创建了新实例，需要重新应用字幕样式
            if (subtitleStyle) {
              console.log('🎬 [VIDEOEDIT] reloadToScene后重新应用字幕样式:', subtitleStyle);
              videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
            }
          }
        }, 500); // 延迟500ms确保场景加载完成

        setHistoryLoaded(false); // 重置标记，避免重复初始化
      } catch (error) {
        console.error('🎬 [VIDEOEDIT] 历史记录播放器初始化失败:', error);
        setHistoryLoaded(false); // 重置标记
      }
    } else if (videoPlayerInstance.current && subtitleStyle) {
      // ✅ 新增：播放器已存在但字幕样式更新了，也要应用
      console.log('🎬 [VIDEOEDIT] 播放器已存在，更新字幕样式:', subtitleStyle);
      videoPlayerInstance.current.updateSubtitleStyle(subtitleStyle);
    } else {
      console.log('🎬 [VIDEOEDIT] 跳过播放器初始化，原因:', {
        historyLoaded,
        hasContainer: !!videoPlayerContainer.current,
        sceneListLength: sceneList.length,
        hasPlayerInstance: !!videoPlayerInstance.current
      });
    }
  }, [historyLoaded, sceneList.length, selectedBgMusic, bgMusicVolume, subtitleStyle]);

  // 监听当前操作的场景图片变化，更新对话框预览
  useEffect(() => {
    if ((isTextToImageDialogOpen || isImageSelectionDialogOpen) && currentSceneId) {
      const currentScene = sceneList.find(item => item.id === currentSceneId);
      if (currentScene) {
        setDialogImages(currentScene.images);
      }
    }
  }, [sceneList.find(item => item.id === currentSceneId)?.images, isTextToImageDialogOpen, isImageSelectionDialogOpen, currentSceneId]);

  return (
    <div className="relative w-full h-full flex flex-col">


      <div id="main-content-container" className="flex-1 flex items-center justify-center py-[4px] h-full">
        {/* 分镜列表区域 */}
        {sceneList.length > 0 && (
          <div id="scene-list-container" className="min-w-[500px] w-[600px] h-[calc(100vh-110px)] flex flex-col items-start">
            <div className="overflow-y-auto w-full h-full px-[20px] pl-[40px]">
              <div className="ml-2 mr-2 space-y-6 pt-6 pb-4">
                {/* 完整分镜卡片组件 */}
                {sceneList.map((item, index) => {
                  // Loading态使用简化版本
                  if (item.status === SceneStatus.LOADING) {
                    return (
                      <div key={item.id} className="w-full min-w-[500px] max-w-[600px] flex flex-col flex-shrink-0">
                        {/* 第一条item上方添加分隔线 */}
                        {index === 0 && (
                          <div className="relative w-full mb-6">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-gray-700/50"></div>
                            </div>
                          </div>
                        )}
                        <div className="relative flex items-center">
                          <div className="absolute top-4 left-[-30px] text-xl font-bold text-white z-10">#{index + 1}</div>

                          {/* 删除按钮与卡片内容垂直居中对齐 */}
                          <button
                            className="absolute top-1/2 left-[-30px] transform -translate-y-1/2 w-6 h-6 rounded-full bg-gray-500/30 hover:bg-gray-500/50 text-gray-400 hover:text-gray-300 flex items-center justify-center transition-colors z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScene(item.id);
                            }}
                            title={t('videoEdit.deleteScene')}
                          >
                            <X className="w-3 h-3" />
                          </button>

                          <div className="flex flex-1 flex-col gap-3 rounded-xl p-4 border border-gray-700/50">
                            <div className="flex flex-row gap-2">
                              {/* 分镜内容部分 */}
                              <div className="flex-[10] flex-shrink-0 flex flex-col gap-2 m-1 justify-center">
                                {/* 旁白部分 */}
                                <div className="bg-gray-700/30 rounded-lg p-3">
                                  <div className="text-xs text-gray-400 mb-1">{t('videoEdit.narration')}</div>
                                  <div className="h-10 bg-gray-600/50 rounded animate-pulse"></div>
                                </div>

                                {/* 游标进度条 */}
                                <div className="mt-4 m-1 pt-1 pl-0.25 pr-0.25">
                                  <div className="relative w-full h-4 bg-gray-700/30 rounded-full">
                                    <div className="absolute inset-y-0 bg-gray-600/50 rounded-full"></div>
                                  </div>
                                  {/* 时间刻度 */}
                                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                                    {[0, 4, 8, 12, 16, 20].map(time => (
                                      <div key={time}>{time}s</div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* 图片部分 */}
                              <div className="flex-[5] flex-shrink-0 flex flex-col justify-center items-center m-1">
                                <div className="w-full">
                                  <div className="bg-gray-700/30 rounded-lg flex items-center justify-center p-2">
                                    <div className="w-full aspect-[1/1] bg-gray-600/50 rounded animate-pulse"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 分镜分隔线 - loading状态只显示横线 */}
                        <div className="relative w-full mt-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700/50"></div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // READY态使用完整卡片
                  return (
                    <div className="w-full min-w-[500px] max-w-[700px] flex flex-col flex-shrink-0 transition-all duration-300 opacity-100">
                      {/* 第一条item上方添加分隔线 - ready状态显示+按钮 */}
                      {index === 0 && (
                        <div className="relative w-full mb-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="flex-1 border-t border-gray-700/50"></div>
                            <button
                              className="mx-6 px-[10px] py-[6px] bg-gray-600/30 hover:bg-gray-600/50 text-gray-400 hover:text-gray-300 rounded-full text-sm font-medium transition-all flex items-center justify-center min-w-[32px] min-h-[32px]"
                              onClick={() => {
                                // 第一条卡片上方的+按钮点击时插入在第0条位置
                                const newId = generateUniqueId();
                                const newItem: SceneItem = {
                                  id: newId,
                                  desc: '',
                                  prompt: '',
                                  dialogue: '',
                                  images: [],
                                  videoUrl: '',
                                  audioUrl: '',
                                  status: SceneStatus.READY,
                                  cursorPosition: { startTime: 0, endTime: 5 },
                                  audioPlayState: false,
                                  videoLoading: false
                                };
                                setSceneList(prev => [newItem, ...prev]);
                              }}
                              title={t('videoEdit.addNewScene')}
                            >
                              +
                            </button>
                            <div className="flex-1 border-t border-gray-700/50"></div>
                          </div>
                        </div>
                      )}
                      <div className="relative flex items-center" tabIndex={0}>
                        <div className="absolute top-4 left-[-30px] text-xl font-bold text-white z-10">#{index + 1}</div>

                        {/* 删除按钮与卡片内容垂直居中对齐 */}
                        <button
                          className="absolute top-1/2 left-[-30px] transform -translate-y-1/2 w-6 h-6 rounded-full bg-gray-500/30 hover:bg-gray-500/50 text-gray-400 hover:text-gray-300 flex items-center justify-center transition-colors z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteScene(item.id);
                          }}
                          title={t('videoEdit.deleteScene')}
                        >
                          <X className="w-3 h-3" />
                        </button>

                        <div className="flex flex-1 flex-col gap-3 rounded-xl p-4 border border-gray-700/50 h-fit min-h-[50px] focus-within:border-blue-400/70">
                          <div className="flex flex-row gap-2">
                            {/* 分镜内容部分 */}
                            <div className="flex-[10] flex-shrink-0 flex flex-col gap-2 m-1 justify-center">

                              {/* 旁白与音频播放 */}
                              <div
                                className="bg-gray-700/30 rounded-lg p-3 text-sm flex items-center cursor-pointer hover:bg-gray-700/50 transition-colors"
                                onClick={() => {
                                  setCurrentNarration(item.dialogue);
                                  setCurrentSceneId(item.id);
                                  setIsVoiceSynthesisDialogOpen(true);
                                }}
                              >
                                <div className="flex-1">
                                  <div className="text-xs text-gray-400 mb-1">{t('videoEdit.narration')}</div>
                                  <div className="text-white text-sm/80 line-clamp-2 overflow-hidden">
                                    {item.dialogue}
                                  </div>
                                </div>

                                <div className="ml-1 mr-1 flex-shrink-0">
                                  <button
                                    className={`aspect-square w-10 h-10 rounded-full flex items-center justify-center transition-all ${item.audioPlayState ? 'bg-blue-500/50 text-white' : 'bg-gray-500/30 text-gray-400 hover:bg-gray-500/50 hover:text-gray-300'
                                      }`}
                                    onClick={(e) => {
                                      e.stopPropagation(); // 阻止事件冒泡到父级div
                                      toggleAudioPlayState(item.id);
                                    }}
                                    disabled={!item.audioUrl || item.audioUrl === ''}
                                  >
                                    {item.audioPlayState ? (
                                      <Pause className="w-4 h-4" />
                                    ) : (
                                      <Play className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* 游标进度条 */}
                              <div className="mt-4 m-1 pt-1 pl-0.25">
                                <div className="relative w-full h-4 bg-gray-700/30 rounded-full">
                                  <div
                                    className="absolute inset-y-0 bg-gray-600/50 rounded-full"
                                    style={{
                                      left: '0px',
                                      width: `calc(${((item.cursorPosition?.endTime ?? 5) - 0) / 20 * 100}% + 8px)`
                                    }}
                                    ref={(el) => {
                                      if (el) {
                                        progressBarFillRefs.current[item.id] = el;
                                      }
                                    }}
                                  />

                                  {/* 结束游标 */}
                                  <div
                                    className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-purple-500/70 hover:bg-purple-500/90 rounded-full cursor-move transition-all duration-200 z-30 border border-white/30 group"
                                    style={{ left: `${(item.cursorPosition?.endTime ?? 5) / 20 * 100}%` }}
                                    title={t('videoEdit.endTime')}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      // 设置拖拽状态
                                      isDraggingRef.current = true;

                                      const cursor = e.currentTarget;
                                      const progressBar = cursor.parentElement as HTMLElement;
                                      const rect = progressBar.getBoundingClientRect();
                                      const tooltip = cursor.querySelector('div:last-child') as HTMLElement;

                                      let hasChanged = false;

                                      const handleMouseMove = (moveEvent: MouseEvent) => {
                                        moveEvent.preventDefault();
                                        let x = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));

                                        // 边界处理：当接近左边或右边时，强制设置为边界值
                                        if (x < 1) x = 0;
                                        if (x > rect.width - 1) x = rect.width;

                                        const percentage = x / rect.width;
                                        const value = percentage * 20;
                                        const roundedValue = Math.max(0, Math.min(20, Number(value.toFixed(2))));

                                        // 从本地 Ref 获取最新位置，避免状态异步问题
                                        const localCursor = latestCursorPositionsRef.current[item.id] || { startTime: 0, endTime: 0 };
                                        const currentValue = localCursor.endTime;

                                        // Only update if value has changed by at least 0.05s to further reduce frequency
                                        if (Math.abs(currentValue - roundedValue) > 0.05) {
                                          hasChanged = true;

                                          // Real-time update tooltip
                                          tooltip.textContent = `${roundedValue.toFixed(1)}s`;

                                          updateCursorPosition(item.id, 'end', roundedValue, true);

                                          // 直接更新游标的DOM位置，避免等待状态更新
                                          cursor.style.left = `${percentage * 100}%`;

                                          // 直接更新进度条填充部分的DOM，避免等待状态更新
                                          const progressBarFill = progressBarFillRefs.current[item.id];
                                          if (progressBarFill) {
                                            progressBarFill.style.width = `calc(${percentage * 100}% + 8px)`;
                                          }
                                        }
                                      }

                                      const handleMouseUp = (upEvent: MouseEvent) => {
                                        upEvent.preventDefault();
                                        document.removeEventListener('mousemove', handleMouseMove);
                                        document.removeEventListener('mouseup', handleMouseUp);

                                        // 清除拖拽状态
                                        isDraggingRef.current = false;

                                        // 只有当鼠标在游标上松开且有变化时才保存
                                        if (hasChanged) {
                                          // 🔧 关键修复：先同步最新的游标位置到sceneList状态
                                          syncCursorToSceneList(item.id);

                                          // 设置跳过标记，避免useEffect重复保存
                                          skipNextSaveRef.current = true;

                                          // 立即保存，用户完成操作（清除现有延时）
                                          if (cursorSaveTimerRef.current) {
                                            clearTimeout(cursorSaveTimerRef.current);
                                            cursorSaveTimerRef.current = null;
                                          }

                                        }
                                      }

                                      // Add listeners to document
                                      document.addEventListener('mousemove', handleMouseMove);
                                      document.addEventListener('mouseup', handleMouseUp);
                                    }}
                                  >
                                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs font-medium text-purple-500/90 whitespace-nowrap transition-all duration-200 opacity-90 hover:opacity-100">
                                      {(item.cursorPosition?.endTime ?? 5).toFixed(1)}s
                                    </div>
                                  </div>

                                  {/* 转场设置按钮 - 已注释掉 */}
                                  {/* {(() => {
                                    const sceneIndex = sceneList.findIndex(scene => scene.id === item.id);
                                    const sceneCount = sceneList.length;
                                    // 检查是否允许设置转场
                                    // 不允许的情况：
                                    // 1. 尾帧
                                    // 2. 只有一个场景的首帧
                                    const isLastScene = sceneIndex === sceneCount - 1;
                                    const isOnlyScene = sceneCount === 1;
                                    const canSetTransition = !(isLastScene || isOnlyScene);

                                    let buttonTitle = t('videoEdit.transitionSetting');
                                    if (isLastScene) {
                                      buttonTitle = t('videoEdit.lastSceneNoTransition');
                                    } else if (isOnlyScene) {
                                      buttonTitle = t('videoEdit.onlySceneNoTransition');
                                    }

                                    return (
                                      <button
                                        className={`absolute top-1/2 right-[-35px] transform -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs transition-all duration-200 z-40 pl-3.75 ${canSetTransition
                                          ? "bg-blue-500/30 hover:bg-blue-500/50"
                                          : "bg-gray-500/30 cursor-not-allowed"
                                          }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (canSetTransition) {
                                            handleTransitionSettingOpen(item.id);
                                          }
                                        }}
                                        disabled={!canSetTransition}
                                        title={buttonTitle}
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                      </button>
                                    );
                                  })()} */}
                                </div>

                                {/* 时间刻度 */}
                                <div className="flex justify-between mt-1 text-xs text-gray-400">
                                  {[0, 4, 8, 12, 16, 20].map(time => (
                                    <div key={time}>{time}s</div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* 图片/视频部分 */}
                            <div className="flex-[5] flex-shrink-0 flex flex-col justify-center items-center m-1">
                              <div className="w-full">
                                <div className="bg-gray-700/30 rounded-lg flex items-center justify-center p-2 relative">
                                  {/* 转换视频/删除视频按钮 - 视频生成时隐藏 */}
                                  {item.videoStatus !== 'processing' && (
                                    <button
                                      className="absolute top-2 right-2 px-2 py-1 text-[11px] rounded-lg bg-blue-500/80 hover:bg-blue-500 text-white transition-colors z-10"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.videoUrl && item.videoUrl !== '') {
                                          // 删除视频
                                          setSceneList(prev => prev.map(scene =>
                                            scene.id === item.id ? { ...scene, videoUrl: '', videoTaskId: undefined, videoStatus: undefined } : scene
                                          ));
                                        } else {
                                          // 转换视频
                                          setCurrentPrompt(item.prompt);
                                          setCurrentSceneId(item.id);
                                          setDialogImages(item.images || []);
                                          setIsImageToVideoDialogOpen(true);
                                        }
                                      }}
                                    >
                                      {item.videoUrl && item.videoUrl !== '' ? t('videoEdit.deleteVideo') : t('videoEdit.convertVideo')}
                                    </button>
                                  )}

                                  {/* 加载状态覆盖层 */}
                                  {item.status === SceneStatus.GENERATING_MEDIA && item.isImageGenerating && (
                                    <div className="absolute inset-0 bg-black/50 z-10 flex flex-col items-center justify-center">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>

                                    </div>
                                  )}

                                  {/* 视频播放 */}
                                  {item.videoUrl && item.videoUrl !== '' ? (
                                    <video
                                      src={item.videoUrl}
                                      className="w-full aspect-[1/1] rounded object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const video = e.currentTarget;
                                        if (video.paused) {
                                          video.play();
                                        } else {
                                          video.pause();
                                        }
                                      }}
                                      controls
                                      controlsList="nodownload"
                                    />
                                  ) : (item.images && item.images.length > 0) ? (
                                    <img
                                      src={item.images[0]}
                                      alt={t('videoEdit.sceneImage', { sceneId: item.id, imageId: 1 })}
                                      className="w-full aspect-[1/1] rounded object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => {
                                        setCurrentPrompt(item.prompt);
                                        setCurrentSceneId(item.id);
                                        setDialogImages(item.images || []);
                                        setIsTextToImageDialogOpen(true);
                                      }}
                                    />
                                  ) : (
                                    <DefaultImageIcon
                                      onClick={() => {
                                        setCurrentPrompt(item.prompt);
                                        setCurrentSceneId(item.id);
                                        setDialogImages(item.images || []);
                                        setIsTextToImageDialogOpen(true);
                                      }}
                                    />
                                  )}

                                  {/* 视频生成状态 */}
                                  {item.videoStatus && item.videoStatus === 'processing' && (
                                    <div className="absolute bottom-2 left-2 px-2 py-1 text-[10px] rounded-full bg-yellow-500/80 text-white">
                                      {t('videoEdit.generatingVideo')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 分镜分隔线和添加按钮 */}
                      <div className="relative w-full mt-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="flex-1 border-t border-gray-700/50"></div>
                          <button
                            className="mx-6 px-[10px] py-[6px] bg-gray-600/30 hover:bg-gray-600/50 text-gray-400 hover:text-gray-300 rounded-full text-sm font-medium transition-all flex items-center justify-center min-w-[32px] min-h-[32px]"
                            onClick={() => handleAddItemAfterId(item.id)}
                            title={t('videoEdit.addNewScene')}
                          >
                            +
                          </button>
                          <div className="flex-1 border-t border-gray-700/50"></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )
        }

        {/* 左侧分隔线 */}
        {
          sceneList.length > 0 && (
            <div className="w-px bg-gray-700/50 self-stretch mx-2"></div>
          )
        }

        {/* 右侧结果区域 */}
        {
          sceneList.length > 0 && (
            <div id="result-area-container" className="flex-[1] min-w-[300px] mx-8 flex flex-col items-center justify-center gap-4 px-2">
              {/* 视频标题显示 */}
              <div
                className="w-full max-w-[400px] text-center cursor-pointer hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
                onClick={handleTitleEditOpen}
              >
                <h2 className="text-xl font-bold text-white">{title || t('videoEdit.clickToEditTitle')}</h2>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-gray-400 hover:text-blue-400 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </div>
              
              {/* 配置按钮行：背景音乐 + 字幕 */}
              <div className="w-full max-w-[400px] flex justify-center items-center gap-4 mb-2">
                {/* 背景音乐按钮 */}
                <button
                  onClick={() => setIsBgMusicDialogOpen(true)}
                  className="flex items-center justify-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <Music className="w-5 h-5" />
                  <span>
                    {selectedBgMusic ? bgMusicList.find(music => music.url === selectedBgMusic)?.name || t('videoEdit.backgroundMusic') : t('videoEdit.backgroundMusic')}
                  </span>
                </button>

                {/* 字幕按钮（新增） */}
                <button
                  onClick={() => setIsSubtitleDialogOpen(true)}
                  className="flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <Type className="w-5 h-5" />
                  <span>
                    {subtitleStyle?.templateId && subtitleTemplates.length > 0
                      ? subtitleTemplates.find(t => t.id === subtitleStyle.templateId)?.name || t('videoEdit.subtitle')
                      : t('videoEdit.subtitle')
                    }
                  </span>
                </button>
              </div>

              {/* 视频播放器容器 */}
              <div
                ref={videoPlayerContainer}
                className="w-full max-w-[400px] aspect-square backdrop-blur-sm border border-gray-700/40 rounded-lg overflow-hidden"
              >
                {/* 默认图片图标 */}
                <DefaultImageIcon className="w-full h-full" />
                {/* 视频播放器将在此处渲染，会覆盖默认图标 */}
              </div>
              {/* 检查是否有视频正在生成 */}
              {!sceneList.some(scene => scene.videoStatus === 'processing') && (
                <div className="flex gap-4 w-full justify-center">
                  <button
                    className="flex-1 max-w-[250px] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md border border-gray-700/50 rounded-lg px-4 py-3 text-white font-medium hover:shadow-lg transition-all"
                    onClick={handleVideoComposition}
                  >
                    {t('videoEdit.compositeVideo')}
                  </button>
                </div>
              )}
            </div>
          )
        }

        {/* 没有场景时的提示 - 居中显示 */}
        {
          sceneList.length === 0 && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-white text-center">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent mb-4"></div>
                  <div className="text-xl mb-2">{t('common.loading')}</div>
                  <div className="text-gray-400">{t('videoEdit.waitForAIGeneration')}</div>
                </div>
              </div>
            </div>
          )
        }
      </div >

      {/* 文生图对话框 */}
      <TextToImageDialog
        isOpen={isTextToImageDialogOpen}
        onClose={() => setIsTextToImageDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialPrompt={currentPrompt}
        initialResolution={resolution}
        initialQuality={quality}
        imageSizes={imageSizes}
        onUpdatePrompt={handleUpdatePrompt}
        onUpdateImages={handleUpdateImages}
        onUpdatePromptSilent={handleUpdatePromptSilent}
        onUpdateResolution={(newResolution) => setResolution(newResolution)}
        onUpdateQuality={(newQuality) => setQuality(newQuality)}
        onSetDialogOperating={(sceneId, isOperating) => {
          if (isOperating) {
            setSceneGeneratingMedia(sceneId, true); // 生成图片时设置isImageGenerating为true
          } else {
            // 生成失败时重置状态为READY
            setSceneList(prev => prev.map(item =>
              item.id === sceneId ? { ...item, status: SceneStatus.READY, isImageGenerating: false } : item
            ));
          }
        }}
        onShowToast={(message) => {
          setToastMessage(message);
          setToastType('success');
          setToastVisible(true);
        }}
        currentImages={dialogImages}
        sceneList={sceneList}
        onUpdateFirstImage={(sceneId, imageUrl) => {
          setSceneList(prev =>
            prev.map(scene => {
              if (scene.id === sceneId) {
                const newImages = [...scene.images];
                if (newImages.length > 0) {
                  newImages[0] = imageUrl;
                } else {
                  newImages.push(imageUrl);
                }
                return { ...scene, images: newImages, isImageGenerating: false };
              }
              return scene;
            })
          );
          // 保存数据到json


        }}
      />

      {/* 语音合成对话框 */}
      <VoiceSynthesisDialog
        isOpen={isVoiceSynthesisDialogOpen}
        onClose={() => setIsVoiceSynthesisDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialNarration={currentNarration}
        initialVoice={sceneList.find(s => s.id === currentSceneId)?.selectedVoice ?? selectedVoice}
        initialVoiceSpeed={sceneList.find(s => s.id === currentSceneId)?.voiceSpeed ?? voiceSpeed}
        voices={voices.map(voice => ({
          ...voice,
          voice_type: voice.voice_type || ''
        }))}
        onUpdateNarration={handleUpdateNarration}
        onUpdateAudio={(sceneId, audioUrl, duration) => {
          setSceneList(prev => prev.map(item => {
            if (item.id === sceneId) {
              const updatedScene = { ...item, audioUrl };
              if (duration && duration > 0) {
                updatedScene.cursorPosition = {
                  startTime: item.cursorPosition?.startTime || 0,
                  endTime: Math.min(duration, 20)
                };
              }
              return updatedScene;
            }
            return item;
          }));
        }}
        onUpdateNarrationSilent={handleUpdateNarrationSilent}
        onUpdateVoice={(newVoice) => {
          setSceneList(prev => prev.map(item => 
            item.id === currentSceneId ? { ...item, selectedVoice: newVoice } : item
          ));
        }}
        onUpdateVoiceSpeed={(newSpeed) => {
          setSceneList(prev => prev.map(item => 
            item.id === currentSceneId ? { ...item, voiceSpeed: newSpeed } : item
          ));
        }}
        onSetDialogOperating={(sceneId, isOperating) => {
          if (isOperating) {
            setSceneGeneratingMedia(sceneId, false);
          }
        }}
        onShowToast={(message) => {
          setToastMessage(message);
          setToastType('success');
          setToastVisible(true);
        }}
      />

      {/* 图片选择对话框 */}
      <ImageSelectionDialog
        isOpen={isImageSelectionDialogOpen}
        onClose={() => setIsImageSelectionDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        imageId={1}
        scene={sceneList.find(item => item.id === currentSceneId) || null}
      />

      {/* 图片转视频对话框 */}
      <ImageToVideoDialog
        isOpen={isImageToVideoDialogOpen}
        onClose={() => setIsImageToVideoDialogOpen(false)}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialPrompt={currentPrompt}
        currentImages={dialogImages}
        onUpdateVideoTaskId={handleUpdateVideoTaskId}
        onUpdateVideoStatus={handleUpdateVideoStatus}
        onShowToast={(message) => {
          setToastMessage(message);
          setToastType('success');
          setToastVisible(true);
        }}
        sceneList={sceneList}
      />

      {/* 标题编辑对话框 */}
      {isTitleEditDialogOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={handleTitleEditClose}
        >
          <div
            className="relative bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-4 shadow-2xl overflow-hidden w-[600px] h-[300px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 border-b border-gray-700/50 pb-2">
              <h2 className="text-xl font-semibold text-white">{t('videoEdit.editTitle')}</h2>
              <button
                className="text-gray-300 hover:text-white transition-colors"
                onClick={handleTitleEditClose}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl p-6 h-[calc(100%-72px)] flex flex-col">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('videoEdit.videoTitle')}</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/70 focus:border-transparent"
                  placeholder={t('videoEdit.enterVideoTitle')}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-4 mt-auto">
                <button
                  className="px-6 py-2 bg-gray-600/50 hover:bg-gray-600/70 text-white rounded-lg transition-colors"
                  onClick={handleTitleEditClose}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="px-6 py-2 bg-blue-500/70 hover:bg-blue-500/90 text-white rounded-lg transition-colors"
                  onClick={handleTitleSave}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 转场设置对话框 */}
      <TransitionSettingDialog
        isOpen={isTransitionSettingDialogOpen}
        onClose={handleTransitionSettingClose}
        sceneId={currentSceneId}
        sceneNumber={sceneList.findIndex(scene => scene.id === currentSceneId) + 1}
        initialTransition={sceneList.find(item => item.id === currentSceneId)?.transition}
        onSaveTransition={handleSaveTransition}
        sceneList={sceneList} // Pass the sceneList for preview
      />

      {/* 背景音乐选择对话框 */}
      <BgMusicDialog
        isOpen={isBgMusicDialogOpen}
        onClose={() => setIsBgMusicDialogOpen(false)}
        t={t}
      >
        <BgMusicSelector
          bgMusicList={bgMusicList}
          selectedBgMusic={selectedBgMusic}
          bgMusicVolume={bgMusicVolume}
          onBgMusicSelect={setSelectedBgMusic}
          onVolumeChange={setBgMusicVolume}
        />
      </BgMusicDialog>

      {/* 字幕配置对话框（新增） */}
      <SubtitleStyleDialog
        isOpen={isSubtitleDialogOpen}
        onClose={() => setIsSubtitleDialogOpen(false)}
        onApply={setSubtitleStyle}
        currentStyle={subtitleStyle}
        t={t}
      />

      {/* Toast 提示组件 */}
      {toastVisible && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastVisible(false)}
        />
      )}
    </div>
  );
});

export default VideoEditPage;
